/**
 * Trading Terminal Component for Hyperliquid
 * Handles order entry, TP/SL, leverage, and position management
 */
class TradingTerminal {
    constructor(container, selectedAsset, availableAssets) {
        this.container = container;
        this.selectedAsset = selectedAsset;
        this.availableAssets = availableAssets;
        this.currentAssetData = null;
        this.currentPosition = null;
        this.availableBalance = 0;

        // Order state
        this.orderType = 'market'; // 'market', 'limit', 'advanced' (PRIMARY TABS)
        this.orderSide = 'long'; // 'long' or 'short' (TOGGLE/SUBTAB)
        this.advancedType = null; // 'chase' or 'evgrid' (selected from advanced dropdown)

        // EV Grid settings
        this.evGridSettings = {
            levels: 3,
            baseTickDistance: 10,
            sizePerLevel: 0,
            anchor: null,  // Mid price anchor - Long below, Short above
            frequency: 120,  // Single frequency value (seconds)
            useAnchor: true  // Whether to use anchor price (if false, quotes around mid)
        };
        this.leverage = 1; // Auto-set to asset max leverage on load/change
        // console.log('🎬 [TERMINAL-INIT] Initial leverage set to:', this.leverage);
        this.isCrossMargin = localStorage.getItem('marginMode') !== 'isolated'; // Default to cross margin
        this.marginPercentage = 100; // Always 100% of available balance
        this.currentPrice = 0; // Current market price for USD conversion
        this.price = '';
        this.size = '0'; // Initialize with 0
        this.sizePercentage = 0;
        this.isUpdatingSizeProgrammatically = false; // Flag to prevent event loop
        this.isSizeInputFocused = false; // Track if user is manually editing size
        this.isSizeManuallySet = false; // Track if user has manually set the size (via slider or input)
        this.sizeUnit = 'asset'; // 'asset' or 'usd'
        this.marginMessageIndex = 0; // Track which margin message to show (alternates)

        // Submit button debounce protection
        this.lastSubmitTime = 0;
        this.submitCooldown = 2000; // 2 seconds

        // TP/SL state
        this.tpslEnabled = false;  // Disabled by default
        this.tpPrice = '';
        this.slPrice = '';
        // TP/SL orders are always limit orders (hardcoded in backend)

        // Chase settings (embedded in panel, not modal)
        this.chaseSettings = {
            tickDistance: 5,
            frequencyRangeMin: 5,
            frequencyRangeMax: 60,
            tpAtrMultiple: 2.0,   // TP at 2x ATR from entry
            slAtrMultiple: 1.5,   // SL at 1.5x ATR from entry
            priceLimitEnabled: false,  // Enable price limit checkbox
            priceLimit: null      // Price limit value (upper for Long, lower for Short)
        };
        this.pendingChases = [];
        this.chaseOrderWatchers = new Map();

        // Advanced options
        this.reduceOnlyEnabled = false;
        this.postOnly = false;
        this.timeInForce = 'GTC'; // 'GTC', 'IOC', 'FOK'
        
        // Bind dropdown click outside handler
        this.handleAdvancedDropdownClickOutside = this.handleAdvancedDropdownClickOutside.bind(this);
        
        // Track dropdown state
        this.isAdvancedDropdownOpen = false;
        this.panelClickHandler = null;

        // Layer settings for limit orders
        this.layerEnabled = false;
        this.layerCount = 3;      // Number of layers
        this.layerTickDistance = 10; // Tick distance between layers

        // Degen mode toggle
        this.isDegenMode = false;  // Track if degen size is active
        
        // Block programmatic slider updates after order submission
        this.blockSliderUpdates = false;
        this.init();
    }

    init() {
        // Store global reference for chase settings change button
        const globalKey = `tradingTerminal_${this.selectedAsset?.replace(/[^a-zA-Z0-9]/g, '_')}`;
        window[globalKey] = this;

        this.updateAssetData();
        this.render();
        this.setupEventListeners();
        this.updateLeverageDisplay();
        this.updateSubmitButtonState(); // Initialize submit button state
        this.updateSizeSlider(); // Initialize slider max attribute after render
        // Don't call loadAccountData() here - it resets balance to 0
        // Balance is managed by updateAvailableBalance() from positions data
    }

    updateAsset(selectedAsset, availableAssets) {
        // console.log(`🔄 [TRADING-TERMINAL] updateAsset called: ${this.selectedAsset} → ${selectedAsset}`);
        this.selectedAsset = selectedAsset;
        this.availableAssets = availableAssets;
        this.updateAssetData();
        this.render();
        this.updateMarginDisplay();
        // console.log(`✅ [TRADING-TERMINAL] Asset updated to: ${this.selectedAsset}`);
    }

    /**
     * Toggle advanced dropdown
     */
    toggleAdvancedDropdown() {
        // Use state flag instead of DOM query since panel moves to document.body when open
        if (this.isAdvancedDropdownOpen) {
            this.closeAdvancedDropdown();
        } else {
            this.openAdvancedDropdown();
        }
    }

    /**
     * Open advanced dropdown
     */
    openAdvancedDropdown() {
        // Prevent double-opening
        if (this.isAdvancedDropdownOpen) return;
        
        const panel = this.container.querySelector('.advanced-dropdown-panel');
        const button = this.container.querySelector('.advanced-dropdown-btn');
        if (!panel || !button) return;
        
        this.isAdvancedDropdownOpen = true;
        button.classList.add('open');
        
        // Append panel to body to escape overflow constraints
        if (panel.parentElement !== document.body) {
            document.body.appendChild(panel);
        }
        
        // Add click listener to panel (remove old one first if exists)
        if (this.panelClickHandler) {
            panel.removeEventListener('click', this.panelClickHandler);
        }
        
        this.panelClickHandler = (e) => {
            const dropdownItem = e.target.closest('.advanced-dropdown-item');
            if (dropdownItem) {
                const selectedType = dropdownItem.dataset.value;
                
                this.advancedType = selectedType;
                this.orderType = 'advanced';
                this.closeAdvancedDropdown();
                
                // Auto-fill settings based on selection
                if (selectedType === 'chase') {
                    this.autofillChasePrice();
                } else if (selectedType === 'evgrid') {
                    this.prefillEvGridAnchor();
                }
                
                this.render();
                this.validateInputs();
            }
        };
        panel.addEventListener('click', this.panelClickHandler);
        
        // Use double requestAnimationFrame to ensure positioning is accurate
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const buttonRect = button.getBoundingClientRect();
                panel.style.position = 'fixed';
                panel.style.display = 'block';
                panel.style.top = `${buttonRect.bottom + 4}px`;
                panel.style.left = `${buttonRect.left}px`;
                panel.style.width = `${buttonRect.width}px`;
                panel.style.zIndex = '10000';
            });
        });
        
        // Add click-outside listener
        setTimeout(() => {
            document.addEventListener('click', this.handleAdvancedDropdownClickOutside);
        }, 0);
    }

    /**
     * Close advanced dropdown
     */
    closeAdvancedDropdown() {
        if (!this.isAdvancedDropdownOpen) return;
        
        const panel = document.querySelector('.advanced-dropdown-panel');
        const button = this.container?.querySelector('.advanced-dropdown-btn');
        
        // Hide panel
        if (panel) {
            panel.style.display = 'none';
            
            // Remove panel click listener
            if (this.panelClickHandler) {
                panel.removeEventListener('click', this.panelClickHandler);
                this.panelClickHandler = null;
            }
            
            // Move panel back to its original container
            const tabsContainer = this.container?.querySelector('.order-type-tabs-container');
            if (tabsContainer && panel.parentElement === document.body) {
                tabsContainer.appendChild(panel);
            }
        }
        
        // Remove open class from button
        if (button) {
            button.classList.remove('open');
        }
        
        // Remove click-outside listener
        document.removeEventListener('click', this.handleAdvancedDropdownClickOutside);
        
        // Update state
        this.isAdvancedDropdownOpen = false;
    }

    /**
     * Handle click outside advanced dropdown
     */
    handleAdvancedDropdownClickOutside(e) {
        const panel = document.querySelector('.advanced-dropdown-panel');
        const button = this.container.querySelector('.advanced-dropdown-btn');
        
        if (!panel || !button) return;
        
        if (!panel.contains(e.target) && !button.contains(e.target)) {
            this.closeAdvancedDropdown();
        }
    }

    updateAssetData() {
        this.currentAssetData = this.availableAssets.find(asset => asset.name === this.selectedAsset);
        if (this.currentAssetData) {
            const oldLeverage = this.leverage;
            // Auto-set to max leverage on asset change for seamless trading
            this.leverage = this.currentAssetData.maxLeverage;
            // console.log('🔄 [ASSET-UPDATE] Leverage set to max:', oldLeverage, '→', this.leverage, 'maxLeverage:', this.currentAssetData.maxLeverage);

            // Cache szDecimals for size formatting (default to 5 if not available)
            this.szDecimals = this.currentAssetData.szDecimals !== undefined ? parseInt(this.currentAssetData.szDecimals) : 5;
        }
    }

    render() {
        // Block all renders during 3s after order submission to prevent slider jump
        if (this.blockSliderUpdates) return;
        
        // Clean up any open dropdown before re-rendering
        if (this.isAdvancedDropdownOpen) {
            this.closeAdvancedDropdown();
        }
        if (!this.container) return;

        // Set default advanced type if not set
        if (this.orderType === 'advanced' && !this.advancedType) {
            this.advancedType = 'chase';
        }

        // Determine if EV Grid mode (neutral - no long/short)
        const isEvGridMode = this.orderType === 'advanced' && this.advancedType === 'evgrid';
        // Determine if Advanced mode (Chase or EVGrid) - for ATR-based TP/SL
        const isAdvancedMode = this.orderType === 'advanced';

        this.container.innerHTML = `
            <div class="trading-terminal-layout">
                <!-- Order Entry Section -->
                <div class="order-entry-section">
                    <!-- TOP BUTTONS: Cross and Leverage -->
                    <div class="top-controls-row">
                        <button class="top-control-btn cross-btn" id="cross-btn">
                            ${this.isCrossMargin ? 'Cross' : 'Isolated'}
                        </button>
                        <button class="top-control-btn leverage-btn" id="leverage-btn">
                            ${this.leverage}x
                        </button>
                    </div>

                    <!-- ORDER TYPE TABS: Market / Limit / Advanced -->
                    <div class="order-type-tabs-container">
                        <button class="order-type-tab ${this.orderType === 'market' ? 'active' : ''}" data-type="market">
                            Market
                        </button>
                        <button class="order-type-tab ${this.orderType === 'limit' ? 'active' : ''}" data-type="limit">
                            Limit
                        </button>
                        <button class="order-type-tab advanced-dropdown-btn ${this.orderType === 'advanced' ? 'active' : ''}" data-type="advanced">
                            <span class="advanced-label">${this.orderType === 'advanced' && this.advancedType ? (this.advancedType === 'chase' ? 'Chase' : 'EV Grid') : 'Advanced'}</span>
                            <svg class="advanced-dropdown-arrow" width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <div class="advanced-dropdown-panel" style="display: none;">
                            <div class="advanced-dropdown-item" data-value="chase">Chase</div>
                            <div class="advanced-dropdown-item" data-value="evgrid">EV Grid</div>
                        </div>
                    </div>

                    <!-- LONG/SHORT TOGGLE (hidden in EV Grid mode) -->
                    ${!isEvGridMode ? `
                    <div class="long-short-selector">
                        <button class="long-short-btn ${this.orderSide === 'long' ? 'active long' : ''}" data-side="long">
                            Long
                        </button>
                        <button class="long-short-btn ${this.orderSide === 'short' ? 'active short' : ''}" data-side="short">
                            Short
                        </button>
                    </div>
                    ` : ''}

                    <!-- CHASE SETTINGS (inline, only visible when Advanced + Chase selected) -->
                    ${this.orderType === 'advanced' && this.advancedType === 'chase' ? `
                        <div class="chase-settings-inline">
                            <h4 class="chase-settings-title static">
                                <span>Chase Configuration</span>
                            </h4>
                            
                            <div class="chase-settings-content expanded">
                                <div class="chase-input-group">
                                    <label for="chase-tick-distance-input" class="label-with-help">
                                        Tick/% Distance
                                        <span class="help-icon" data-tooltip="Distance from best bid/ask. Enter a number for ticks (e.g., 5) or % for percentage (e.g., 0.5%).">?</span>
                                    </label>
                                    <input type="text"
                                           id="chase-tick-distance-input"
                                           class="chase-input"
                                           value="${this.chaseSettings.tickDistance}"
                                           placeholder="5 or 0.5%">
                                </div>
                                
                                <div class="chase-frequency-group">
                                    <label class="label-with-help">
                                        Modification Frequency (seconds)
                                        <span class="help-icon" data-tooltip="Random delay between order modifications. Helps avoid predictable patterns.">?</span>
                                    </label>
                                    <div class="frequency-inputs">
                                        <div class="freq-input-wrapper">
                                            <label for="chase-freq-min-input" class="freq-label">Min</label>
                                            <input type="number"
                                                   id="chase-freq-min-input"
                                                   class="chase-input freq-input"
                                                   min="5"
                                                   value="${this.chaseSettings.frequencyRangeMin}"
                                                   placeholder="5">
                                        </div>
                                        <span class="freq-separator">-</span>
                                        <div class="freq-input-wrapper">
                                            <label for="chase-freq-max-input" class="freq-label">Max</label>
                                            <input type="number"
                                                   id="chase-freq-max-input"
                                                   class="chase-input freq-input"
                                                   min="5"
                                                   value="${this.chaseSettings.frequencyRangeMax}"
                                                   placeholder="60">
                                        </div>
                                    </div>
                                </div>

                                <div class="chase-input-group">
                                    <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem;">
                                        <input type="checkbox" id="chase-price-limit-enabled" ${this.chaseSettings.priceLimitEnabled ? 'checked' : ''}>
                                        <label for="chase-price-limit-enabled" style="margin: 0; cursor: pointer;" class="label-with-help">
                                            Enable Price Limit
                                            <span style="color: #888; font-size: 0.75rem;" id="chase-price-limit-hint">(${this.orderSide === 'long' ? 'upper' : 'lower'} bound)</span>
                                        </label>
                                    </div>
                                    <input type="number"
                                           id="chase-price-limit-input"
                                           class="chase-input"
                                           step="any"
                                           value="${this.chaseSettings.priceLimit || ''}"
                                           placeholder="Stop chase if price exceeds"
                                           style="display: ${this.chaseSettings.priceLimitEnabled ? 'block' : 'none'};">
                                </div>

                            </div>
                        </div>
                    ` : ''}

                    <!-- EV GRID SETTINGS (only visible when Advanced + EV Grid selected) -->
                    ${isEvGridMode ? `
                        <div class="evgrid-settings-inline">
                            <h4 class="evgrid-settings-title">
                                <span>EV Grid Configuration</span>
                            </h4>

                            <div class="evgrid-settings-content">
                                <div class="evgrid-row">
                                    <div class="evgrid-input-group">
                                        <label for="evgrid-levels-input">Levels per Side</label>
                                        <select id="evgrid-levels-input" class="evgrid-select">
                                            <option value="3" ${this.evGridSettings.levels === 3 ? 'selected' : ''}>3 levels (6 orders)</option>
                                            <option value="6" ${this.evGridSettings.levels === 6 ? 'selected' : ''}>6 levels (12 orders)</option>
                                            <option value="10" ${this.evGridSettings.levels === 10 ? 'selected' : ''}>10 levels (20 orders)</option>
                                        </select>
                                    </div>
                                    <div class="evgrid-input-group">
                                        <label for="evgrid-tick-distance-input">Base Tick/% Distance</label>
                                        <input type="text" id="evgrid-tick-distance-input" class="evgrid-input" value="${this.evGridSettings.baseTickDistance}" placeholder="10 or 1%">
                                    </div>
                                </div>

                                <div class="evgrid-levels-preview" id="evgrid-levels-preview">
                                    Levels: ${this._getEvGridLevelsPreview()}
                                </div>

                                <div class="evgrid-input-group full-width">
                                    <label for="evgrid-size-input">Size per Level (${this.selectedAsset})</label>
                                    <input type="number" id="evgrid-size-input" class="evgrid-input" min="0.001" step="any" value="${this.evGridSettings.sizePerLevel || ''}" placeholder="e.g., 0.1">
                                </div>

                                <div class="evgrid-row">
                                    <div class="evgrid-input-group" style="flex: 1;">
                                        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem;">
                                            <input type="checkbox" id="evgrid-use-anchor" ${this.evGridSettings.useAnchor ? 'checked' : ''}>
                                            <label for="evgrid-use-anchor" style="margin: 0; cursor: pointer;">Use Anchor Price</label>
                                        </div>
                                        <input type="number" id="evgrid-anchor-input" class="evgrid-input" step="any" value="${this.evGridSettings.anchor || ''}" placeholder="Mid price" ${!this.evGridSettings.useAnchor ? 'disabled' : ''}>
                                    </div>
                                </div>

                                <div class="evgrid-row">
                                    <div class="evgrid-input-group" style="flex: 1;">
                                        <label for="evgrid-frequency-input">Frequency (seconds)</label>
                                        <input type="number" id="evgrid-frequency-input" class="evgrid-input" min="1" value="${this.evGridSettings.frequency}" placeholder="120">
                                    </div>
                                </div>

                                <div class="evgrid-summary" id="evgrid-summary">
                                    ${this._getEvGridSummary()}
                                </div>
                            </div>
                        </div>
                    ` : ''}

                    <!-- PRICE AND SIZE INPUTS (hidden in EV Grid mode) -->
                    ${!isEvGridMode ? `
                    <div class="price-size-row ${this.orderType === 'market' ? 'market-mode' : ''}">
                        ${this.orderType !== 'market' ? `
                        <div class="price-input-group unified-price-input">
                            <div class="unified-input-container">
                                <span class="input-label">Price (USD)</span>
                                <input type="text" id="price-input" class="price-input-integrated" placeholder="0.00" value="${this.getDisplayPrice()}">
                                ${this.orderType === 'limit' ? '<button type="button" class="mid-price-btn" id="quick-price-btn" title="Fill mid price">Mid</button>' : ''}
                            </div>
                            <div class="input-validation" id="price-validation"></div>
                        </div>
                        ` : ''}

                        <div class="size-input-group unified-size-input ${this.orderType === 'market' ? 'full-width' : ''}">
                            <div class="unified-input-container">
                                <span class="input-label">Size</span>
                                <input type="text" id="size-input" class="size-input-integrated" placeholder="0" value="${this.getDisplaySize()}">
                                <div class="unit-dropdown-container">
                                    <button type="button" class="unit-dropdown-btn" id="size-unit-toggle">
                                        <span class="unit-text">${this.sizeUnit === 'usd' ? 'USDC' : this.selectedAsset}</span>
                                        <svg class="dropdown-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <div class="unit-dropdown-menu hidden" id="size-unit-menu">
                                        <div class="unit-option ${this.sizeUnit === 'asset' ? 'selected' : ''}" data-value="asset">${this.selectedAsset}</div>
                                        <div class="unit-option ${this.sizeUnit === 'usd' ? 'selected' : ''}" data-value="usd">USDC</div>
                                    </div>
                                </div>
                            </div>
                            <div class="input-validation" id="size-validation"></div>
                        </div>
                    </div>



                    <div class="size-slider-section">
                        <div class="size-slider-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                            <label for="size-slider" style="margin-bottom: 0;">Size: <span id="size-slider-value">${this.size || '0.00'} ${this.selectedAsset}</span></label>
                            <button type="button" class="risk-size-btn degen-btn-small ${this.isDegenMode ? 'active' : ''}" id="degen-size-btn" title="${this.isDegenMode ? 'Reset to default size' : '2.5% Risk'}">
                                ${this.isDegenMode ? 'Nah not really!' : 'FEELING DEGEN?'}
                            </button>
                        </div>
                        <div class="size-slider-container">
                            <input type="range" id="size-slider" class="size-slider" 
                                   min="0" max="${this.calculateMaxSizeUnrestricted() || 1}" 
                                   value="${parseFloat(this.size) || 0}" step="${this.getSizeStep()}">
                            <div class="size-slider-markers">
                                <span class="marker low">0%</span>
                                <span class="marker medium">50%</span>
                                <span class="marker high">100%</span>
                            </div>
                        </div>
                    </div>
                    <div class="margin-percentage-display" style="display: none;">
                        <!-- Margin usage is always 100%, hidden from UI but used in calculations -->
                    </div>
                    ` : ''}
                </div>

                <!-- Reduce-Only Section -->
                <div class="reduce-only-section">
                    <label class="reduce-only-checkbox-label">
                        <input type="checkbox" id="reduce-only-enabled" ${this.reduceOnlyEnabled ? 'checked' : ''}>
                        <span class="checkmark"></span>
                        Reduce-Only
                        <span class="help-icon" data-tooltip="Only reduces existing position, won't open opposite position">?</span>
                    </label>
                </div>

                <!-- TP/SL Section -->
                <div class="tpsl-section">
                    <div class="tpsl-header">
                        <label class="tpsl-checkbox-label">
                            <input type="checkbox" id="tpsl-enabled" ${this.tpslEnabled ? 'checked' : ''}>
                            <span class="checkmark"></span>
                            Enable TP/SL
                        </label>
                    </div>

                    <div class="tpsl-controls ${this.tpslEnabled ? 'enabled' : 'disabled'}">
                        ${isAdvancedMode || isEvGridMode ? `
                        <!-- ATR Multiple inputs for Chase/EVGrid - TP/SL placed on fill -->
                        <div class="tpsl-inputs tpsl-atr-mode">
                            <div class="tp-input-group">
                                <label for="tp-atr-multiple" class="label-with-help">TP (ATR Multiple)
                                    <span class="help-icon" data-tooltip="TP placed at entry ± (ATR × multiple) on fill">?</span>
                                </label>
                                <div class="tpsl-input-row">
                                    <input type="number" id="tp-atr-multiple" class="tp-input atr-input" placeholder="2.0"
                                           value="${this.chaseSettings.tpAtrMultiple}" step="0.1" min="0.1"
                                           ${!this.tpslEnabled ? 'disabled' : ''}>
                                    <span class="atr-suffix">x ATR</span>
                                </div>
                            </div>

                            <div class="sl-input-group">
                                <label for="sl-atr-multiple" class="label-with-help">SL (ATR Multiple)
                                    <span class="help-icon" data-tooltip="SL placed at entry ± (ATR × multiple) on fill">?</span>
                                </label>
                                <div class="tpsl-input-row">
                                    <input type="number" id="sl-atr-multiple" class="sl-input atr-input" placeholder="1.5"
                                           value="${this.chaseSettings.slAtrMultiple}" step="0.1" min="0.1"
                                           ${!this.tpslEnabled ? 'disabled' : ''}>
                                    <span class="atr-suffix">x ATR</span>
                                </div>
                            </div>
                        </div>
                        ` : `
                        <!-- Price inputs for Market/Limit orders -->
                        <div class="tpsl-inputs">
                            <div class="tp-input-group">
                                <label for="tp-price">Take Profit (USD)</label>
                                <div class="tpsl-input-row">
                                    <input type="text" id="tp-price" class="tp-input" placeholder="0.00" value="${this.tpPrice}" ${!this.tpslEnabled ? 'disabled' : ''}>
                                    <button type="button" class="tpsl-quick-btn tp-quick-btn" id="tp-quick-btn" title="EV AUTO TP" ${!this.tpslEnabled ? 'disabled' : ''}>⚡</button>
                                </div>
                                <div class="input-validation" id="tp-validation"></div>
                            </div>

                            <div class="sl-input-group">
                                <label for="sl-price">Stop Loss (USD)</label>
                                <div class="tpsl-input-row">
                                    <input type="text" id="sl-price" class="sl-input" placeholder="0.00" value="${this.slPrice}" ${!this.tpslEnabled ? 'disabled' : ''}>
                                    <button type="button" class="tpsl-quick-btn sl-quick-btn" id="sl-quick-btn" title="EV AUTO SL" ${!this.tpslEnabled ? 'disabled' : ''}>⚡</button>
                                </div>
                                <div class="input-validation" id="sl-validation"></div>
                            </div>
                        </div>
                        `}
                    </div>
                </div>

                <!-- Layer Section (only for limit orders) - Positioned below TP/SL -->
                ${this.orderType === 'limit' ? `
                <div class="layer-section">
                    <div class="layer-header">
                        <label class="layer-checkbox-label">
                            <input type="checkbox" id="layer-enabled" ${this.layerEnabled ? 'checked' : ''}>
                            <span class="checkmark"></span>
                            Enable Layers
                        </label>
                    </div>

                    <div class="layer-controls ${this.layerEnabled ? 'enabled' : 'disabled'}">
                        <div class="layer-inputs">
                            <div class="layer-input-group">
                                <label for="layer-count">Number of Layers</label>
                                <input type="number" id="layer-count" class="layer-input" min="2" max="20"
                                       value="${this.layerCount}" ${!this.layerEnabled ? 'disabled' : ''}>
                            </div>
                            <div class="layer-input-group">
                                <label for="layer-tick-distance">Tick/% Distance</label>
                                <input type="text" id="layer-tick-distance" class="layer-input" placeholder="10 or 1%"
                                       value="${this.layerTickDistance}" ${!this.layerEnabled ? 'disabled' : ''}>
                            </div>
                        </div>
                        <div class="layer-hint">
                            Size per layer: ${(parseFloat(this.size) / this.layerCount || 0).toFixed(5)} ${this.selectedAsset}
                        </div>
                    </div>
                </div>
                ` : ''}


                <!-- Position & Balance Info -->
                ${this.currentPosition ? `
                <div class="position-info">
                    <div class="current-position">
                        <div class="position-header">Current Position</div>
                        <div class="position-details">
                            <div class="position-item">
                                <span class="label">Size:</span>
                                <span class="value">${this.currentPosition.size} ${this.selectedAsset}</span>
                            </div>
                            <div class="position-item">
                                <span class="label">Entry Price:</span>
                                <span class="value">$${this.currentPosition.entryPrice}</span>
                            </div>
                            <div class="position-item">
                                <span class="label">PnL:</span>
                                <span class="value ${this.currentPosition.pnl >= 0 ? 'positive' : 'negative'}">
                                    $${this.currentPosition.pnl.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                ` : ''}

                <!-- Submit Button - Fixed at bottom -->
                <div class="submit-section fixed-submit-section">
                    ${isEvGridMode ? `
                    <div class="position-info" style="margin-bottom: 12px; padding: 0.75rem;">
                        <div class="balance-display balance-single-line">
                            <span class="label" id="margin-label">Margin Required:</span>
                            <span class="value" id="margin-required">$0.00</span>
                            <span class="balance-separator">•</span>
                            <span class="label">Available:</span>
                            <span class="value" id="available-balance">$${this.availableBalance.toFixed(2)}</span>
                        </div>
                    </div>
                    <button class="submit-btn evgrid-btn" id="start-evgrid-btn">
                        <span class="submit-text" id="submit-text">
                            Start EV Grid
                        </span>
                    </button>
                    ` : `
                    <div class="position-info" style="margin-bottom: 12px; padding: 0.75rem;">
                        <div class="balance-display balance-single-line">
                            <span class="label" id="margin-label">Margin Required:</span>
                            <span class="value" id="margin-required">$0.00</span>
                            <span class="balance-separator">•</span>
                            <span class="label">Available:</span>
                            <span class="value" id="available-balance">$${this.availableBalance.toFixed(2)}</span>
                        </div>
                    </div>
                    <button class="submit-btn ${this.orderSide}" id="submit-order">
                        <span class="submit-text" id="submit-text">
                            ${this.orderSide === 'long' ? 'Long' : 'Short'} ${this.selectedAsset}
                            ${this.orderType === 'advanced' && this.advancedType === 'chase' ? ' (Chase)' : ''}
                        </span>
                    </button>
                    `}
                </div>
            </div>

            <!-- Leverage Modal -->
            <div class="leverage-modal-overlay" id="leverage-modal-overlay" style="display: none;">
                <div class="leverage-modal">
                    <div class="leverage-modal-header">
                        <h3>Set Leverage</h3>
                        <button class="leverage-modal-close" id="leverage-modal-close">&times;</button>
                    </div>
                    <div class="leverage-modal-content">
                        <div class="leverage-section-modal">
                            <label for="leverage-slider-modal">Leverage: <span id="leverage-value-modal">${this.leverage}x</span></label>
                            <div class="leverage-slider-container">
                                <input type="range" id="leverage-slider-modal" class="leverage-slider" 
                                       min="1" max="${this.currentAssetData?.maxLeverage || 50}" 
                                       value="${this.leverage}" step="1">
                                <div class="leverage-markers">
                                    <span class="marker low">1x</span>
                                    <span class="marker medium">${Math.floor((1 + (this.currentAssetData?.maxLeverage || 50)) / 2)}x</span>
                                    <span class="marker high">${this.currentAssetData?.maxLeverage || 50}x</span>
                                </div>
                            </div>
                        </div>
                        <div class="leverage-modal-actions">
                            <button class="leverage-modal-btn confirm-btn" id="leverage-modal-confirm">OK</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Margin Mode Confirmation Modal -->
            <div class="leverage-modal-overlay" id="margin-mode-modal-overlay" style="display: none;">
                <div class="leverage-modal margin-mode-modal">
                    <div class="leverage-modal-header">
                        <h3>Change Margin Mode</h3>
                        <button class="leverage-modal-close" id="margin-mode-modal-close">&times;</button>
                    </div>
                    <div class="leverage-modal-content">
                        <div class="margin-mode-warning">
                            <p class="warning-text">
                                Switch to <strong id="margin-mode-target">Isolated</strong> margin?
                            </p>
                            <p class="warning-description">
                                This applies globally across all assets until changed.
                            </p>
                            <ul class="margin-mode-info">
                                <li><strong>Cross:</strong> Shares collateral across positions</li>
                                <li><strong>Isolated:</strong> Limits risk per position</li>
                            </ul>
                        </div>
                        <div class="leverage-modal-actions">
                            <button class="leverage-modal-btn cancel-btn" id="margin-mode-modal-cancel">Cancel</button>
                            <button class="leverage-modal-btn confirm-btn" id="margin-mode-modal-confirm">Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Initialize leverage modal
        this.initializeLeverageModal();
    }

    setupEventListeners() {
        if (!this.container) return;

        // Event delegation for clicks - single listener handles all buttons
        this.container.addEventListener('click', (e) => {
            // ADVANCED DROPDOWN ITEM SELECTION
            const dropdownItem = e.target.closest('.advanced-dropdown-item');
            if (dropdownItem) {
                const selectedType = dropdownItem.dataset.value;
                
                this.advancedType = selectedType;
                this.orderType = 'advanced';
                this.closeAdvancedDropdown();
                
                // Auto-fill settings based on selection
                if (selectedType === 'chase') {
                    this.autofillChasePrice();
                } else if (selectedType === 'evgrid') {
                    this.prefillEvGridAnchor();
                }
                
                this.render();
                this.validateInputs();
                return;
            }

            // ORDER TYPE TABS (Market/Limit/Advanced)
            const orderTypeTab = e.target.closest('.order-type-tab');
            if (orderTypeTab) {
                const clickedType = orderTypeTab.dataset.type;
                
                // Special handling for Advanced - toggle dropdown
                if (clickedType === 'advanced') {
                    this.toggleAdvancedDropdown();
                    return;
                }
                
                // Normal tabs (Market/Limit)
                this.orderType = clickedType;
                this.advancedType = null;
                this.render();
                this.validateInputs();

                // Update quick price button for limit orders
                if (this.orderType === 'limit') {
                    this.updateQuickPriceButton();
                }
                return;
            }

            // LONG/SHORT BUTTONS (replaces old side buttons)
            const longShortBtn = e.target.closest('.long-short-btn');
            if (longShortBtn) {
                this.orderSide = longShortBtn.dataset.side; // 'long' or 'short'

                // Recalculate chase price if currently in chase mode
                if (this.orderType === 'advanced' && this.advancedType === 'chase') {
                    this.autofillChasePrice();
                }

                this.render();
                this.validateInputs();

                // Update quick price button and price input when side changes (for limit orders)
                if (this.orderType === 'limit') {
                    this.updateQuickPriceButton();
                    this.fillQuickPrice();  // Also update price input with new side-specific price
                }
                return;
            }

            // Percentage buttons
            const percentBtn = e.target.closest('.percentage-btn');
            if (percentBtn) {
                const percentage = parseInt(percentBtn.dataset.percentage);
                this.setSizeByPercentage(percentage);
                return;
            }

            // Quick price button (limit orders)
            if (e.target.closest('#quick-price-btn')) {
                this.fillQuickPrice();
                return;
            }

            // TP quick button - auto-fill +2% for long, -2% for short
            if (e.target.closest('#tp-quick-btn')) {
                this.fillQuickTPSL('tp');
                return;
            }

            // SL quick button - auto-fill -2% for long, +2% for short
            if (e.target.closest('#sl-quick-btn')) {
                this.fillQuickTPSL('sl');
                return;
            }

            // Degen size button - toggle between 2.5% risk and reset
            if (e.target.closest('#degen-size-btn')) {
                this.toggleDegenMode();
                return;
            }

            // Submit button
            if (e.target.closest('#submit-order')) {
                this.submitOrder();
                return;
            }

            // Grid button - open grid strategy modal
            if (e.target.closest('#start-grid-btn')) {
                this.openGridModal();
                return;
            }

            // EV Grid start button
            if (e.target.closest('#start-evgrid-btn')) {
                e.stopPropagation();
                e.preventDefault();
                this.startEvGrid();
                return;
            }

            // Leverage button - open modal
            if (e.target.closest('#leverage-btn')) {
                this.openLeverageModal();
                return;
            }

            // Cross margin button - toggle between Cross and Isolated
            if (e.target.closest('#cross-btn')) {
                this.toggleMarginMode();
                return;
            }

            // Leverage modal close
            if (e.target.closest('#leverage-modal-close')) {
                this.closeLeverageModal();
                return;
            }

            // Leverage modal confirm
            if (e.target.closest('#leverage-modal-confirm')) {
                this.confirmLeverageModal();
                return;
            }

            // Close modal on overlay click
            if (e.target.id === 'leverage-modal-overlay') {
                this.closeLeverageModal();
                return;
            }

            // Margin mode modal close
            if (e.target.closest('#margin-mode-modal-close')) {
                this.closeMarginModeModal();
                return;
            }

            // Margin mode modal cancel
            if (e.target.closest('#margin-mode-modal-cancel')) {
                this.closeMarginModeModal();
                return;
            }

            // Margin mode modal confirm
            if (e.target.closest('#margin-mode-modal-confirm')) {
                this.confirmMarginModeModal();
                return;
            }

            // Close margin mode modal on overlay click
            if (e.target.id === 'margin-mode-modal-overlay') {
                this.closeMarginModeModal();
                return;
            }

            // Size Unit Toggle
            const unitToggle = e.target.closest('#size-unit-toggle');
            if (unitToggle) {
                const menu = this.container.querySelector('#size-unit-menu');
                if (menu) {
                    menu.classList.toggle('hidden');
                    unitToggle.classList.toggle('open');
                }
                return;
            }

            // Size Unit Option Selection
            const unitOption = e.target.closest('.unit-option');
            if (unitOption) {
                const newValue = unitOption.dataset.value;
                if (newValue !== this.sizeUnit) {
                    this.sizeUnit = newValue;

                    // Update UI text
                    const toggleText = this.container.querySelector('#size-unit-toggle .unit-text');
                    if (toggleText) toggleText.textContent = this.sizeUnit === 'usd' ? 'USDC' : this.selectedAsset;

                    // Update selected class
                    const options = this.container.querySelectorAll('.unit-option');
                    options.forEach(opt => {
                        if (opt.dataset.value === this.sizeUnit) opt.classList.add('selected');
                        else opt.classList.remove('selected');
                    });

                    // Update input value based on new unit
                    const input = this.container.querySelector('#size-input');
                    if (input) {
                        this.isUpdatingSizeProgrammatically = true;
                        input.value = this.getDisplaySize();
                        setTimeout(() => this.isUpdatingSizeProgrammatically = false, 0);
                    }
                }
                // Close menu and remove open class
                const menu = this.container.querySelector('#size-unit-menu');
                const button = this.container.querySelector('#size-unit-toggle');
                if (menu) menu.classList.add('hidden');
                if (button) button.classList.remove('open');
                return;
            }

            // Close dropdown if clicking outside
            if (!e.target.closest('.unit-dropdown-container')) {
                const menu = this.container.querySelector('#size-unit-menu');
                const button = this.container.querySelector('#size-unit-toggle');
                if (menu && !menu.classList.contains('hidden')) {
                    menu.classList.add('hidden');
                    if (button) button.classList.remove('open');
                }
            }
        });

        // Change events for dropdowns and checkboxes
        this.container.addEventListener('change', (e) => {
            // Order type selection is now handled by CustomDropdown onChange callback
            // No need for this event listener

            // TP/SL checkbox
            if (e.target.id === 'tpsl-enabled') {
                this.tpslEnabled = e.target.checked;
                this.render();
                setTimeout(() => {
                    this.validatePrice();
                    this.validateTPSL();
                }, 0);
                return;
            }

            // Layer enabled checkbox
            if (e.target.id === 'layer-enabled') {
                this.layerEnabled = e.target.checked;
                this.render();
                return;
            }

            // Reduce-only checkbox
            if (e.target.id === 'reduce-only-enabled') {
                this.reduceOnlyEnabled = e.target.checked;
                return;
            }

            // Chase frequency auto-adjust on change (not input) to fix keystroke bug
            // When user finishes typing, auto-adjust min/max if they conflict
            if (e.target.id === 'chase-freq-min-input') {
                const newMin = parseInt(e.target.value) || 5;
                if (newMin > this.chaseSettings.frequencyRangeMax) {
                    this.chaseSettings.frequencyRangeMax = newMin;
                    const maxInput = this.container.querySelector('#chase-freq-max-input');
                    if (maxInput) maxInput.value = newMin;
                }
                return;
            }

            if (e.target.id === 'chase-freq-max-input') {
                const newMax = parseInt(e.target.value) || 60;
                if (newMax < this.chaseSettings.frequencyRangeMin) {
                    this.chaseSettings.frequencyRangeMin = newMax;
                    const minInput = this.container.querySelector('#chase-freq-min-input');
                    if (minInput) minInput.value = newMax;
                }
                return;
            }
        });

        // Input events for all text inputs and sliders
        this.container.addEventListener('input', (e) => {
            // Price input
            if (e.target.id === 'price-input' && this.orderType !== 'market') {
                this.price = e.target.value;
                this.validatePrice();
                this.validateTPSL();
                this.calculateMargin();
                return;
            }

            // Size input
            if (e.target.id === 'size-input') {
                if (this.isUpdatingSizeProgrammatically) return;

                let val = e.target.value;
                if (this.sizeUnit === 'usd') {
                    // Convert USD input to Asset size
                    // Handle empty or invalid
                    if (!val || isNaN(parseFloat(val))) {
                        this.size = '0';
                    } else if (this.currentPrice > 0) {
                        const usdAmount = parseFloat(val);
                        this.size = (usdAmount / this.currentPrice).toFixed(this.szDecimals);
                    }
                } else {
                    this.size = val;
                }

                this.isSizeManuallySet = true; // Mark as manually set by user
                this.validateSize();
                this.calculateMargin();
                // Custom subtitle update could go here

                // Update size slider when input changes
                this.updateSizeSlider();
                return;
            }



            // Size slider
            if (e.target.id === 'size-slider') {
                const sliderValue = parseFloat(e.target.value);
                this.size = this.formatSizeToDecimals(sliderValue);
                this.isSizeManuallySet = true; // Mark as manually set by user
                const sizeSliderValue = this.container.querySelector('#size-slider-value');
                if (sizeSliderValue) {
                    sizeSliderValue.textContent = `${this.size} ${this.selectedAsset}`;
                }
                // Update size input field
                const sizeInput = this.container.querySelector('#size-input');
                if (sizeInput && !this.isUpdatingSizeProgrammatically) {
                    this.isUpdatingSizeProgrammatically = true;
                    sizeInput.value = this.getDisplaySize();
                    setTimeout(() => {
                        this.isUpdatingSizeProgrammatically = false;
                    }, 0);
                }
                this.validateSize();
                this.calculateMargin();
                this.updateSizeUSDDisplay();
                return;
            }

            // Leverage slider (in modal)
            if (e.target.id === 'leverage-slider-modal') {
                const rawValue = e.target.value;
                const requestedLeverage = parseInt(rawValue);
                const maxLeverage = this.currentAssetData?.maxLeverage || 50;

                // Constrain leverage to asset's max
                const tempLeverage = Math.min(requestedLeverage, maxLeverage);

                // Update modal display value
                const leverageValue = this.container.querySelector('#leverage-value-modal');
                if (leverageValue) {
                    leverageValue.textContent = `${tempLeverage}x`;
                }
                return;
            }

            // Margin percentage slider removed - margin usage is always 100%

            // TP input
            if (e.target.id === 'tp-price') {
                this.tpPrice = e.target.value;
                this.validateTPSL();
                this.validatePrice();
                return;
            }

            // SL input
            if (e.target.id === 'sl-price') {
                this.slPrice = e.target.value;
                this.validateTPSL();
                this.validatePrice();
                return;
            }

            // TP ATR multiple input (for Chase/EVGrid)
            if (e.target.id === 'tp-atr-multiple') {
                this.chaseSettings.tpAtrMultiple = parseFloat(e.target.value) || 2.0;
                return;
            }

            // SL ATR multiple input (for Chase/EVGrid)
            if (e.target.id === 'sl-atr-multiple') {
                this.chaseSettings.slAtrMultiple = parseFloat(e.target.value) || 1.5;
                return;
            }

            // Chase settings inputs (only active when chase is selected)
            if (e.target.id === 'chase-tick-distance-input') {
                // Store raw value - can be number (ticks) or "X%" (percentage)
                this.chaseSettings.tickDistance = e.target.value.trim() || '5';
                this.validateChaseSettings();
                return;
            }

            // Chase frequency inputs: only update value on input, auto-adjust on change
            // (Moved auto-adjust to 'change' event to fix keystroke bug where typing "10"
            // would set min=1 because the first keystroke "1" triggered auto-adjust)
            if (e.target.id === 'chase-freq-min-input') {
                let newMin = parseInt(e.target.value) || 5;
                this.chaseSettings.frequencyRangeMin = newMin;
                this.validateChaseSettings();
                return;
            }

            if (e.target.id === 'chase-freq-max-input') {
                let newMax = parseInt(e.target.value) || 60;
                this.chaseSettings.frequencyRangeMax = newMax;
                this.validateChaseSettings();
                return;
            }

            // Chase price limit checkbox
            if (e.target.id === 'chase-price-limit-enabled') {
                this.chaseSettings.priceLimitEnabled = e.target.checked;
                const priceInput = this.container.querySelector('#chase-price-limit-input');
                if (priceInput) {
                    priceInput.style.display = e.target.checked ? 'block' : 'none';
                }
                return;
            }

            // Chase price limit input
            if (e.target.id === 'chase-price-limit-input') {
                this.chaseSettings.priceLimit = parseFloat(e.target.value) || null;
                return;
            }

            // EV Grid settings inputs
            if (e.target.id === 'evgrid-tick-distance-input') {
                // Store raw value - can be number (ticks) or "X%" (percentage)
                this.evGridSettings.baseTickDistance = e.target.value.trim() || '10';
                this.updateEvGridPreview();
                return;
            }

            if (e.target.id === 'evgrid-size-input') {
                this.evGridSettings.sizePerLevel = parseFloat(e.target.value) || 0;
                this.updateEvGridPreview();
                return;
            }

            if (e.target.id === 'evgrid-use-anchor') {
                this.evGridSettings.useAnchor = e.target.checked;
                const anchorInput = this.container.querySelector('#evgrid-anchor-input');
                if (anchorInput) anchorInput.disabled = !e.target.checked;
                return;
            }

            if (e.target.id === 'evgrid-anchor-input') {
                this.evGridSettings.anchor = parseFloat(e.target.value) || null;
                return;
            }

            if (e.target.id === 'evgrid-frequency-input') {
                this.evGridSettings.frequency = parseInt(e.target.value) || 120;
                return;
            }

            // Layer settings inputs (for limit orders)
            if (e.target.id === 'layer-count') {
                this.layerCount = parseInt(e.target.value) || 3;
                // Clamp to valid range
                this.layerCount = Math.max(2, Math.min(20, this.layerCount));
                // Update layer hint
                const layerHint = this.container.querySelector('.layer-hint');
                if (layerHint) {
                    layerHint.textContent = `Size per layer: ${(parseFloat(this.size) / this.layerCount || 0).toFixed(5)} ${this.selectedAsset}`;
                }
                return;
            }

            if (e.target.id === 'layer-tick-distance') {
                // Store raw value - can be number (ticks) or "X%" (percentage)
                this.layerTickDistance = e.target.value.trim() || '10';
                return;
            }
        });

        // Change events for EV Grid levels dropdown
        this.container.addEventListener('change', (e) => {
            if (e.target.id === 'evgrid-levels-input') {
                this.evGridSettings.levels = parseInt(e.target.value) || 3;
                this.updateEvGridPreview();
                return;
            }
        });

        // Focus/blur for size input - use focusin/focusout which bubble
        this.container.addEventListener('focusin', (e) => {
            if (e.target.id === 'size-input') {
                this.isSizeInputFocused = true;
            }
        });

        this.container.addEventListener('focusout', (e) => {
            if (e.target.id === 'size-input') {
                this.isSizeInputFocused = false;
                // Update size slider when input loses focus
                this.updateSizeSlider();
            }
        });
    }



    updateLeverageDisplay() {
        // Update leverage button text
        const leverageBtn = this.container.querySelector('#leverage-btn');
        if (leverageBtn) {
            leverageBtn.textContent = `${this.leverage}x`;
        }
    }

    toggleMarginMode() {
        // Show confirmation modal instead of immediate toggle
        this.openMarginModeModal();
    }

    updateLeverageSlider() {
        // Update leverage slider in modal if it exists
        const leverageSlider = this.container.querySelector('#leverage-slider-modal');
        const leverageHighMarker = this.container.querySelector('#leverage-modal-overlay .leverage-markers .marker.high');

        if (leverageSlider && this.currentAssetData) {
            leverageSlider.max = this.currentAssetData.maxLeverage;
            leverageSlider.value = Math.min(this.leverage, this.currentAssetData.maxLeverage);
            this.leverage = parseInt(leverageSlider.value);
        }

        if (leverageHighMarker && this.currentAssetData) {
            leverageHighMarker.textContent = `${this.currentAssetData.maxLeverage}x`;
        }

        this.updateLeverageDisplay();
    }

    setSizeByPercentage(percentage) {
        // Calculate size based on available balance and leverage
        const maxSize = (this.availableBalance * this.leverage) / (this.getCurrentPrice() || 1);
        const calculatedSize = (maxSize * percentage) / 100;
        // Round to asset's szDecimals instead of significant digits
        this.size = this.formatSizeToDecimals(calculatedSize);

        const sizeInput = this.container.querySelector('#size-input');
        if (sizeInput) {
            this.isUpdatingSizeProgrammatically = true;
            sizeInput.value = this.getDisplaySize();
            setTimeout(() => {
                this.isUpdatingSizeProgrammatically = false;
            }, 0);
        }

        this.validateSize();
        this.calculateMargin();
        this.updateSizeUSDDisplay();
    }

    setSizeByMarginPercentage() {
        // Margin usage is always 100%, so this method now sets size to max
        // Don't auto-update if user is manually editing or has manually set the size
        if (this.isSizeInputFocused || this.isSizeManuallySet) {
            // Only update the slider max value, but keep the current size value
            this.updateSizeSlider();
            return;
        }

        // Calculate size based on 100% margin usage
        const marginToUse = this.availableBalance; // Always 100%
        const maxSize = (marginToUse * this.leverage) / (this.getCurrentPrice() || 1);

        // Round to asset's szDecimals (e.g., BTC=5, ETH=4) instead of significant digits
        this.size = this.formatSizeToDecimals(maxSize);

        const sizeInput = this.container.querySelector('#size-input');
        if (sizeInput) {
            this.isUpdatingSizeProgrammatically = true;
            sizeInput.value = this.getDisplaySize();
            setTimeout(() => {
                this.isUpdatingSizeProgrammatically = false;
            }, 0);
        } else {
            // console.warn('[TERMINAL] ⚠️ sizeInput element NOT FOUND!');
        }

        // Update size slider
        this.updateSizeSlider();

        this.validateSize();
        this.calculateMargin();
        this.updateSizeUSDDisplay();
    }


    updateMarginDisplay() {
        const marginDisplayLine = this.container.querySelector('#margin-display-line');

        if (marginDisplayLine) {
            const usdMargin = (this.availableBalance * this.marginPercentage) / 100;
            const assetSize = this.currentPrice > 0 ? (usdMargin / this.currentPrice * this.leverage) : 0;
            marginDisplayLine.textContent = `USD MARGIN: $${usdMargin.toFixed(2)} x ${this.leverage} ≈ ${assetSize.toFixed(4)} ${this.selectedAsset}`;
        }
    }

    getCurrentPrice() {
        if (this.orderType === 'market') {
            // Use current market price from orderbook
            let currentPrice = this.currentAssetData?.currentPrice || 0;
            if (typeof currentPrice === 'string') {
                currentPrice = parseFloat(currentPrice) || 0;
            }
            return currentPrice;
        } else if (this.orderType === 'advanced' && this.advancedType === 'evgrid') {
            // EV Grid: use market price for margin calculations
            let marketPrice = this.currentAssetData?.currentPrice || 0;
            if (typeof marketPrice === 'string') {
                marketPrice = parseFloat(marketPrice) || 0;
            }
            return marketPrice;
        } else {
            const limitPrice = parseFloat(this.price) || 0;

            // If limit price is 0 but TP/SL enabled, use market price for directional validation
            if (limitPrice === 0 && this.tpslEnabled && (this.tpPrice || this.slPrice)) {
                let marketPrice = this.currentAssetData?.currentPrice || 0;
                if (typeof marketPrice === 'string') {
                    marketPrice = parseFloat(marketPrice) || 0;
                }
                return marketPrice;
            }

            return limitPrice;
        }
    }

    formatPriceDisplay(price) {
        if (!price || price <= 0) return '0.00';

        // Convert to number if string
        const num = typeof price === 'string' ? parseFloat(price) : price;

        // Format with smart decimal places: min 2, up to 5 significant
        const formatted = num.toFixed(5);

        // Remove trailing zeros but keep at least 2 decimal places
        return formatted.replace(/(\.\d{2})\d*?0+$/, '$1').replace(/\.$/, '');
    }

    getDisplayPrice() {
        if (this.orderType === 'market') {
            // Show current market price for market orders with 5 sig digits
            const currentPrice = this.currentAssetData?.currentPrice;
            if (currentPrice && typeof currentPrice === 'number') {
                return this.formatToSignificantDigits(currentPrice, 5);
            } else if (currentPrice && typeof currentPrice === 'string') {
                return this.formatToSignificantDigits(parseFloat(currentPrice), 5);
            } else {
                return '0';
            }
        } else {
            return this.price;
        }
    }

    getMarketOrderPrice() {
        let currentPrice = this.currentAssetData?.currentPrice || 0;

        // Handle string prices
        if (typeof currentPrice === 'string') {
            currentPrice = parseFloat(currentPrice) || 0;
        }

        if (currentPrice === 0) return 0;

        const slippage = 0.001; // 0.1% slippage buffer for market orders

        if (this.orderSide === 'long') {
            // Long orders: price above market to ensure fill
            return currentPrice * (1 + slippage);
        } else {
            // Short orders: price below market to ensure fill
            return currentPrice * (1 - slippage);
        }
    }

    /**
     * Auto-fill chase price based on current market price and direction
     * ONLY called when in Chase mode (advanced + chase)
     * Long: -0.5 ATR from current price (buy slightly below mid)
     * Short: +0.5 ATR from current price (sell slightly above mid)
     * Falls back to 0.5% if ATR unavailable
     */
    async autofillChasePrice() {
        // Strict guard: ONLY execute if in chase mode
        if (this.orderType !== 'advanced' || this.advancedType !== 'chase') {
            return;
        }

        // Get current market price
        let currentPrice = this.currentAssetData?.currentPrice || 0;

        // Handle string prices
        if (typeof currentPrice === 'string') {
            currentPrice = parseFloat(currentPrice) || 0;
        }

        // Validate price exists
        if (!currentPrice || currentPrice <= 0) {
            // console.warn('[CHASE] Cannot autofill - no current price available');
            return;
        }

        // Calculate chase price with 0.5 ATR offset (fallback to 0.5%)
        let chasePrice;
        let offsetDesc = '';

        // Try ATR-based calculation first
        if (window.ATRService) {
            const atrData = await window.ATRService.getATR(this.selectedAsset);
            if (atrData && atrData.atr) {
                const atrOffset = atrData.atr * 0.5; // 0.5x ATR
                if (this.orderSide === 'long') {
                    chasePrice = currentPrice - atrOffset;  // -0.5 ATR for long
                } else {
                    chasePrice = currentPrice + atrOffset;  // +0.5 ATR for short
                }
                offsetDesc = `${this.orderSide === 'long' ? '-' : '+'}0.5 ATR ($${atrOffset.toFixed(2)})`;
            }
        }

        // Fallback to 0.5% if ATR unavailable
        if (!chasePrice) {
            if (this.orderSide === 'long') {
                chasePrice = currentPrice * 0.995;  // -0.5% for long
            } else {
                chasePrice = currentPrice * 1.005;  // +0.5% for short
            }
            offsetDesc = `${this.orderSide === 'long' ? '-0.5%' : '+0.5%'}`;
        }

        // Format to 5 significant digits for clean display
        this.price = this.formatToSignificantDigits(chasePrice, 5);

        // Update the price input directly (async, so render() already completed)
        const priceInput = this.container?.querySelector('#price-input');
        if (priceInput) {
            priceInput.value = this.price;
        }
    }

    /**
     * Update the quick price button for limit orders
     * Uses ATR-based offset: -0.5 ATR for long, +0.5 ATR for short
     * Falls back to 0.5% if ATR unavailable
     */
    updateQuickPriceButton() {
        // Only for limit orders
        if (this.orderType !== 'limit') {
            return;
        }

        // Get current price from WebSocket (last trade)
        let currentPrice = this.currentAssetData?.currentPrice || 0;
        if (typeof currentPrice === 'string') {
            currentPrice = parseFloat(currentPrice) || 0;
        }

        if (!currentPrice || currentPrice <= 0) {
            this._hideQuickPriceButton();
            return;
        }

        let calculatedPrice;
        let tooltipText;

        // Try to use cached ATR (sync, instant)
        const cachedATR = window.ATRService?.getCachedATR?.(this.selectedAsset);
        if (cachedATR && cachedATR.atr > 0) {
            const atrOffset = cachedATR.atr * 0.5;  // 0.5x ATR
            if (this.orderSide === 'long') {
                calculatedPrice = currentPrice - atrOffset;  // -0.5 ATR for long
            } else {
                calculatedPrice = currentPrice + atrOffset;  // +0.5 ATR for short
            }
            tooltipText = `${this.orderSide === 'long' ? '-' : '+'}0.5 ATR ($${atrOffset.toFixed(2)})`;
        } else {
            // Fallback to 0.5% if ATR not cached
            if (this.orderSide === 'long') {
                calculatedPrice = currentPrice * 0.995;  // -0.5% for long
            } else {
                calculatedPrice = currentPrice * 1.005;  // +0.5% for short
            }
            tooltipText = `${this.orderSide === 'long' ? '-0.5%' : '+0.5%'}`;

            // Trigger async ATR fetch for next update (non-blocking)
            if (window.ATRService?.getATR) {
                window.ATRService.getATR(this.selectedAsset).catch(() => { });
            }
        }

        // Format price
        const formattedPrice = this.formatToSignificantDigits(calculatedPrice, 5);
        this._storedQuickPrice = formattedPrice;

        // Update button - price and style based on side
        const btn = this.container.querySelector('#quick-price-btn');
        const container = this.container.querySelector('#quick-price-container');

        if (btn) {
            // Keep "Mid" text but update tooltip with calculated price
            btn.textContent = 'Mid';
            btn.className = 'mid-price-btn';  // Keep mid-price-btn class
            btn.title = `Fill ${formattedPrice} (${tooltipText})`;
        }
        // Remove old container logic - Mid button is now inline

        // Auto-fill price input if empty (instant pre-fill from Fast Price button)
        const priceInput = this.container.querySelector('#price-input');
        if (priceInput && !priceInput.value) {
            this.price = formattedPrice;
            priceInput.value = formattedPrice;
        }
    }

    /**
     * Hide the quick price button (for non-limit orders)
     */
    _hideQuickPriceButton() {
        const container = this.container.querySelector('#quick-price-container');
        if (container) container.style.display = 'none';
    }

    /**
     * Fill the quick price into the price input
     */
    fillQuickPrice() {
        if (!this._storedQuickPrice || this.orderType !== 'limit') {
            return;
        }

        const priceInput = this.container.querySelector('#price-input');
        if (priceInput) {
            this.price = this._storedQuickPrice;
            priceInput.value = this._storedQuickPrice;

            // Trigger validation and recalculations
            this.validatePrice();
            this.validateTPSL();
            this.calculateMargin();
            this.updateSizeUSDDisplay();

            // console.log(`[TERMINAL] Quick price filled: ${this._storedQuickPrice}`);
        }
    }

    /**
     * Fill TP or SL using ATR-based calculation
     * TP: +2x ATR for long, -2x ATR for short
     * SL: -1.5x ATR for long, +1.5x ATR for short
     * Falls back to ±2% if ATR unavailable
     */
    async fillQuickTPSL(type) {
        if (!this.tpslEnabled) return;

        try {
            // Get current mid price from WebSocket or API
            let midPrice = this.currentAssetData?.currentPrice;
            if (!midPrice) {
                const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(this.selectedAsset);
                midPrice = bidAsk?.mid;
            }

            if (!midPrice) {
                // console.error('[TERMINAL] Could not get mid price for TP/SL');
                return;
            }

            midPrice = parseFloat(midPrice);
            const isLong = this.orderSide === 'long';

            let price;

            // Try ATR-based calculation first
            if (window.ATRService) {
                const tpslData = await window.ATRService.getTPSLRounded(this.selectedAsset, midPrice, isLong);
                if (tpslData && !tpslData.isFallback) {
                    price = type === 'tp' ? tpslData.tp : tpslData.sl;
                    // console.log(`[TERMINAL] ATR-based ${type.toUpperCase()}: ${price} (ATR=${tpslData.atr?.toFixed(4)}, ${tpslData.atrPct?.toFixed(2)}%)`);
                }
            }

            // Fallback to fixed percentage if ATR unavailable
            if (!price) {
                if (type === 'tp') {
                    // TP: +2% for long, -2% for short
                    price = isLong ? midPrice * 1.02 : midPrice * 0.98;
                } else {
                    // SL: -2% for long, +2% for short
                    price = isLong ? midPrice * 0.98 : midPrice * 1.02;
                }
                // Get decimals from mid price
                const priceStr = String(midPrice);
                const decimals = priceStr.includes('.') ? priceStr.split('.')[1].length : 2;
                price = parseFloat(price.toFixed(decimals));
                // console.log(`[TERMINAL] Fallback 2% ${type.toUpperCase()}: ${price}`);
            }

            // Fill the input
            const inputId = type === 'tp' ? 'tp-price' : 'sl-price';
            const input = this.container.querySelector(`#${inputId}`);
            if (input) {
                input.value = price;
                if (type === 'tp') {
                    this.tpPrice = price.toString();
                } else {
                    this.slPrice = price.toString();
                }
                this.validateTPSL();
            }
        } catch (error) {
            // console.error(`[TERMINAL] Error filling quick ${type}:`, error);
        }
    }

    /**
     * Fill size based on risk percentage using ATR
     * Risk calculation: riskAmount = balance * riskPercent
     * SL distance = 1.5x ATR (as percentage of price)
     * Notional size = riskAmount / SL%
     * Asset size = notional / price
     * For EVGrid: divide by number of levels
     */
    fillRiskBasedSize(riskPercent = 0.005) {
        try {
            // Get account balance (sync - instant)
            let accountValue = 0;
            if (window.BrowserPositionTracker?.accountSummary) {
                accountValue = window.BrowserPositionTracker.accountSummary.accountValue || 0;
            } else if (window.lastPositionsData?.accountSummary) {
                accountValue = window.lastPositionsData.accountSummary.accountValue || 0;
            }

            if (accountValue <= 0) {
                // console.warn('[TERMINAL] No account balance available for risk calculation');
                return;
            }

            // Get current price (sync - instant)
            let currentPrice = this.currentAssetData?.currentPrice;
            if (!currentPrice || currentPrice <= 0) {
                // console.warn('[TERMINAL] No price available for risk calculation');
                return;
            }
            currentPrice = parseFloat(currentPrice);

            // Get ATR for SL calculation - use cached ATR first (sync, instant)
            let slPercent = 0.015; // Default 1.5% if ATR unavailable
            const cachedATR = window.ATRService?.getCachedATR?.(this.selectedAsset);
            if (cachedATR && cachedATR.atr > 0) {
                // SL is 1.5x ATR - ensure minimum of 0.1% to prevent division by zero
                slPercent = Math.max(0.001, (cachedATR.atr * 1.5) / currentPrice);
            } else {
                // Trigger async ATR fetch for next time (non-blocking)
                if (window.ATRService?.getATR) {
                    window.ATRService.getATR(this.selectedAsset).catch(() => { });
                }
            }

            // Ensure slPercent has a minimum value to prevent division by zero
            slPercent = Math.max(0.001, slPercent);

            // Calculate risk amount
            const riskAmount = accountValue * riskPercent;

            // Calculate notional size: risk / SL%
            let notionalSize = riskAmount / slPercent;

            // For EVGrid, divide by number of levels
            const isEvGrid = this.orderType === 'advanced' && this.advancedType === 'evgrid';
            if (isEvGrid) {
                const levels = this.evGridSettings.levels || 3;
                notionalSize = notionalSize / levels;
            }

            // Convert to asset size
            let assetSize = notionalSize / currentPrice;

            // Round to appropriate decimals
            const szDecimals = this.szDecimals || 5;
            assetSize = parseFloat(assetSize.toFixed(szDecimals));

            // Update size
            this.size = assetSize.toString();
            this.isSizeManuallySet = true;

            // Update UI (instant, no notifications)
            const sizeInput = this.container.querySelector('#size-input');
            if (sizeInput) sizeInput.value = this.getDisplaySize();

            const sliderValue = this.container.querySelector('#size-slider-value');
            if (sliderValue) sliderValue.textContent = `${this.size} ${this.selectedAsset}`;

            const slider = this.container.querySelector('#size-slider');
            if (slider) slider.value = parseFloat(this.size);

            this.updateSizeUSDDisplay();
            this.calculateMargin();
            this.validateSize();

        } catch (error) {
            // console.error('[TERMINAL] Error calculating risk-based size:', error);
        }
    }

    calculateMargin() {
        const price = this.getCurrentPrice();
        let size = parseFloat(this.size) || 0;
        let labelText = 'Margin Required:';

        // EV Grid mode: calculate total margin for all orders
        const isEvGridMode = this.orderType === 'advanced' && this.advancedType === 'evgrid';
        if (isEvGridMode) {
            const sizePerLevel = this.evGridSettings.sizePerLevel || 0;
            const levels = this.evGridSettings.levels || 3;
            const totalOrders = levels * 2; // BUY + SELL sides
            size = sizePerLevel * totalOrders;
            labelText = 'Margin Required:';
        }
        // Limit Layers mode: divide total size by layer count
        else if (this.layerEnabled && this.layerCount > 1) {
            size = size / this.layerCount;
            labelText = 'Margin Required per Layer:';
        }

        const marginRequired = (price * size) / this.leverage;

        const marginElement = this.container.querySelector('#margin-required');
        if (marginElement) {
            marginElement.textContent = `$${this.formatToSignificantDigits(marginRequired, 6)}`;
        }

        // Update label text dynamically
        const labelElement = this.container.querySelector('#margin-label');
        if (labelElement) {
            labelElement.textContent = labelText;
        }

        this.updateSizeUSDDisplay();
        // Update submit button state when margin changes
        this.updateSubmitButtonState();
    }

    validatePrice() {
        const priceValidation = this.container.querySelector('#price-validation');
        if (!priceValidation) return;

        // Skip validation for market orders since price is auto-calculated
        if (this.orderType === 'market') {
            priceValidation.textContent = '';
            priceValidation.className = 'input-validation';
            return;
        }

        const price = parseFloat(this.price);
        const priceEmpty = !this.price || this.price === '' || isNaN(price) || price === 0;

        // Allow empty price ONLY if TP/SL enabled with valid prices (for adding TP/SL to existing position)
        if (priceEmpty && this.tpslEnabled && (this.tpPrice || this.slPrice)) {
            priceValidation.textContent = 'TP/SL Only (No Entry Order)';
            priceValidation.className = 'input-validation'; // No error - this is valid
            return;
        }

        // Normal limit order validation - only show error for non-empty invalid values
        if (!priceEmpty && price <= 0) {
            priceValidation.textContent = 'Invalid price';
            priceValidation.className = 'input-validation error';
        } else {
            priceValidation.textContent = '';
            priceValidation.className = 'input-validation';
        }
    }

    validateSize() {
        const sizeValidation = this.container.querySelector('#size-validation');
        if (!sizeValidation) return;

        const size = parseFloat(this.size);

        // Always clear first to avoid stale states
        sizeValidation.textContent = '';
        sizeValidation.className = 'input-validation';

        // Only set error if actually invalid
        if (isNaN(size) || size <= 0) {
            sizeValidation.textContent = 'Size must be greater than 0';
            sizeValidation.className = 'input-validation error';
        }
    }

    validateTPSL() {
        const tpValidation = this.container.querySelector('#tp-validation');
        const slValidation = this.container.querySelector('#sl-validation');

        if (!tpValidation || !slValidation) return;

        // Skip validation if TP/SL is disabled
        if (!this.tpslEnabled) {
            tpValidation.textContent = '';
            tpValidation.className = 'input-validation';
            slValidation.textContent = '';
            slValidation.className = 'input-validation';
            return;
        }

        const currentPrice = this.getCurrentPrice();
        const tpPrice = parseFloat(this.tpPrice);
        const slPrice = parseFloat(this.slPrice);

        // Validate TP
        if (this.tpPrice && (isNaN(tpPrice) || tpPrice <= 0)) {
            tpValidation.textContent = 'Invalid TP price';
            tpValidation.className = 'input-validation error';
        } else if (this.tpPrice && currentPrice) {
            const isValidTP = this.orderSide === 'long' ? tpPrice > currentPrice : tpPrice < currentPrice;
            if (!isValidTP) {
                tpValidation.textContent = `TP must be ${this.orderSide === 'long' ? 'above' : 'below'} entry price`;
                tpValidation.className = 'input-validation error';
            } else {
                tpValidation.textContent = '';
                tpValidation.className = 'input-validation';
            }
        } else {
            tpValidation.textContent = '';
            tpValidation.className = 'input-validation';
        }

        // Validate SL
        if (this.slPrice && (isNaN(slPrice) || slPrice <= 0)) {
            slValidation.textContent = 'Invalid SL price';
            slValidation.className = 'input-validation error';
        } else if (this.slPrice && currentPrice) {
            const isValidSL = this.orderSide === 'long' ? slPrice < currentPrice : slPrice > currentPrice;
            if (!isValidSL) {
                slValidation.textContent = `SL must be ${this.orderSide === 'long' ? 'below' : 'above'} entry price`;
                slValidation.className = 'input-validation error';
            } else {
                slValidation.textContent = '';
                slValidation.className = 'input-validation';
            }
        } else {
            slValidation.textContent = '';
            slValidation.className = 'input-validation';
        }
    }

    validateInputs() {
        this.validatePrice();
        this.validateSize();
        this.validateTPSL();
        this.validateChaseSettings(); // Add chase validation
    }

    /**
     * Validate chase settings
     */
    validateChaseSettings() {
        // Only validate if chase is active
        if (this.orderType !== 'advanced' || this.advancedType !== 'chase') {
            return true;
        }

        const tickInput = this.container.querySelector('#chase-tick-distance-input');
        const minInput = this.container.querySelector('#chase-freq-min-input');
        const maxInput = this.container.querySelector('#chase-freq-max-input');

        let hasError = false;

        // Validate tick distance (supports both ticks and percentage)
        if (tickInput) {
            const rawValue = tickInput.value.trim().replace(',', '.');
            const isPercent = rawValue.endsWith('%');

            if (isPercent) {
                // Percentage mode: validate as float between 0.01% and 10%
                const percentValue = parseFloat(rawValue.replace('%', ''));
                if (isNaN(percentValue) || percentValue <= 0 || percentValue > 10) {
                    tickInput.classList.add('error');
                    hasError = true;
                } else {
                    tickInput.classList.remove('error');
                }
            } else {
                // Tick mode: validate as integer between 1 and 10000
                const tick = parseInt(rawValue);
                if (isNaN(tick) || tick < 1 || tick > 10000) {
                    tickInput.classList.add('error');
                    hasError = true;
                } else {
                    tickInput.classList.remove('error');
                }
            }
        }

        // Validate frequency range (only check for valid numbers and min <= max)
        if (minInput && maxInput) {
            const min = parseInt(minInput.value);
            const max = parseInt(maxInput.value);

            if (isNaN(min) || min < 1) {
                minInput.classList.add('error');
                hasError = true;
            } else {
                minInput.classList.remove('error');
            }

            if (isNaN(max) || max < 1) {
                maxInput.classList.add('error');
                hasError = true;
            } else {
                maxInput.classList.remove('error');
            }

            if (!isNaN(min) && !isNaN(max) && min > max) {
                minInput.classList.add('error');
                maxInput.classList.add('error');
                hasError = true;
            }
        }

        return !hasError;
    }

    formatToSignificantDigits(num, sigDigits) {
        if (num === 0) return '0';

        const absNum = Math.abs(num);

        // BTC exception: if price >= 10000, show whole numbers only (no decimals)
        if (absNum >= 10000) {
            return Math.round(num).toString();
        }

        // For other assets: use significant digits
        const magnitude = Math.floor(Math.log10(absNum));
        const precision = sigDigits - magnitude - 1;

        if (precision < 0) {
            // Instead of scientific notation, show with 2 decimals
            return num.toFixed(2);
        } else {
            return num.toFixed(Math.max(0, precision));
        }
    }

    /**
     * Format size to asset's szDecimals (e.g., BTC=5, ETH=4, DOGE=0)
     * This matches Hyperliquid's size precision requirements
     */
    formatSizeToDecimals(size) {
        if (size === null || size === undefined || isNaN(size)) {
            return '0';
        }

        const numSize = typeof size === 'string' ? parseFloat(size) : size;

        if (isNaN(numSize) || numSize <= 0) {
            return '0';
        }

        // Use szDecimals from current asset (default to 5 if not set)
        const decimals = this.szDecimals !== undefined ? this.szDecimals : 5;

        // Round to the specified decimal places
        const rounded = parseFloat(numSize.toFixed(decimals));

        // Convert back to string, removing unnecessary trailing zeros
        return rounded.toString();
    }

    /**
     * Calculate maximum size based on 100% margin usage
     */
    calculateMaxSize() {
        const currentPrice = this.getCurrentPrice();
        if (!currentPrice || currentPrice <= 0 || !this.availableBalance || this.availableBalance <= 0) {
            return 0;
        }
        // Margin usage is always 100%
        const marginToUse = this.availableBalance; // 100% of available balance
        const maxSize = (marginToUse * this.leverage) / currentPrice;
        return maxSize;
    }

    /**
     * Get step value for size slider based on asset's szDecimals
     */
    getSizeStep() {
        const decimals = this.szDecimals !== undefined ? this.szDecimals : 5;
        return Math.pow(10, -decimals);
    }

    /**
     * Update size slider position based on current size value
     */
    updateSizeSlider() {
        const sizeSlider = this.container.querySelector('#size-slider');
        const sizeSliderValue = this.container.querySelector('#size-slider-value');

        if (sizeSlider) {
            const currentSize = parseFloat(this.size) || 0;
            const maxSize = this.calculateMaxSizeUnrestricted(); // Use unrestricted max

            // Don't clamp - allow user to go to max even if it exceeds balance
            sizeSlider.value = currentSize;
            sizeSlider.max = maxSize; // Update max in case balance/leverage changed
            sizeSlider.step = this.getSizeStep();

            // Update markers
            const markers = sizeSlider.parentElement.querySelectorAll('.size-slider-markers .marker');
            if (markers.length >= 3) {
                markers[0].textContent = '0%';
                markers[1].textContent = '50%';
                markers[2].textContent = '100%';
            }
        }

        if (sizeSliderValue) {
            sizeSliderValue.textContent = `${this.size || '0.00'} ${this.selectedAsset}`;
        }

        // Update submit button state based on margin validation
        this.updateSubmitButtonState();
    }

    /**
     * Calculate maximum size based on 100% margin usage (unrestricted - always allows full range)
     */
    calculateMaxSizeUnrestricted() {
        const currentPrice = this.getCurrentPrice();
        if (!currentPrice || currentPrice <= 0 || !this.availableBalance || this.availableBalance <= 0) {
            return 0;
        }
        // Always allow 100% margin usage - no restrictions
        const marginToUse = this.availableBalance; // 100% of available balance
        const maxSize = (marginToUse * this.leverage) / currentPrice;
        return maxSize;
    }

    /**
     * Check if user has enough margin for the current order
     */
    hasEnoughMargin() {
        // Reduce-only orders don't need margin (closing positions)
        if (this.reduceOnlyEnabled) {
            return true;
        }

        const price = this.getCurrentPrice();
        const size = parseFloat(this.size) || 0;
        if (!price || price <= 0 || size <= 0) {
            return false;
        }
        const marginRequired = (price * size) / this.leverage;
        return marginRequired <= this.availableBalance;
    }

    /**
     * Update submit button state based on margin validation
     */
    updateSubmitButtonState() {
        const submitBtn = this.container.querySelector('#submit-order');
        const submitText = this.container.querySelector('#submit-text');

        if (!submitBtn || !submitText) return;

        const hasMargin = this.hasEnoughMargin();
        const size = parseFloat(this.size) || 0;
        const price = this.getCurrentPrice();

        if (!hasMargin && size > 0 && price > 0) {
            // Not enough margin - disable button and show message
            submitBtn.disabled = true;
            submitBtn.classList.add('insufficient-margin');
            // Add side-specific class for styling
            if (this.orderSide === 'long') {
                submitBtn.classList.add('insufficient-margin-long');
                submitBtn.classList.remove('insufficient-margin-short');
            } else {
                submitBtn.classList.add('insufficient-margin-short');
                submitBtn.classList.remove('insufficient-margin-long');
            }
            // Always show "Too broke for this"
            submitText.textContent = 'Too broke for this';
        } else {
            // Enough margin - enable button and show normal text
            submitBtn.disabled = false;
            submitBtn.classList.remove('insufficient-margin', 'insufficient-margin-long', 'insufficient-margin-short');
            const orderTypeText = this.orderType === 'advanced' && this.advancedType === 'chase' ? ' (Chase)' : '';
            submitText.textContent = `${this.orderSide === 'long' ? 'Long' : 'Short'} ${this.selectedAsset}${orderTypeText}`;
        }
    }

    /**
     * Calculate USD value of current size
     */
    calculateSizeUSD() {
        const size = parseFloat(this.size) || 0;
        const price = this.getCurrentPrice() || 0;
        const usdValue = size * price;
        return usdValue.toFixed(2);
    }

    /**
     * Get the actual order size in asset units
     */
    getOrderSizeInAsset() {
        return parseFloat(this.size) || 0;
    }


    /**
     * Toggle degen mode - switches between 2.5% risk size and default (0.5% risk)
     */
    toggleDegenMode() {
        if (this.isDegenMode) {
            // Switch back to default (0.5% risk)
            this.isDegenMode = false;
            this.fillRiskBasedSize(0.005); // 0.5% risk
        } else {
            // Enable degen mode (2.5% risk)
            this.isDegenMode = true;
            this.fillRiskBasedSize(0.025); // 2.5% risk
        }

        // Update button UI
        const btn = this.container.querySelector('#degen-size-btn');
        if (btn) {
            btn.className = `risk-size-btn degen-btn-small ${this.isDegenMode ? 'active' : ''}`;
            btn.title = this.isDegenMode ? 'Reset to default size' : '2.5% Risk';
            btn.innerHTML = this.isDegenMode ? 'Nah not really!' : 'FEELING DEGEN?';
        }
    }


    /**
     * Update USD display element
     */
    updateSizeUSDDisplay() {
        const sizeUSDElement = this.container.querySelector('#size-usd-display');
        if (sizeUSDElement) {
            const usdValue = this.calculateSizeUSD();
            sizeUSDElement.textContent = `≈ $${usdValue}`;
        }
    }

    async loadAccountData() {
        try {
            // Will be replaced with WebSocket balance updates
            this.availableBalance = 0;
            this.updateBalanceDisplay();
            this.calculateMargin();
            this.subscribeToBalanceUpdates();
        } catch (error) {
            this.availableBalance = 0;
        }
    }

    subscribeToBalanceUpdates() {
        // Will be replaced with WebSocket balance updates
    }

    /**
     * Update available balance from positions-live.js data
     */
    updateAvailableBalance(balance) {
        // console.log('[TERMINAL] 💰 updateAvailableBalance called with:', balance);
        // Update for any finite numeric value, including 0 or negative
        const num = typeof balance === 'string' ? parseFloat(balance) : balance;
        if (Number.isFinite(num)) {
            this.availableBalance = num;
            // console.log('[TERMINAL] 💰 Balance set to:', this.availableBalance);
            this.updateBalanceDisplay();
            this.updateMarginDisplay();

            // ALWAYS update slider max attribute when balance changes (unless blocked after order)
            if (!this.blockSliderUpdates) {
                const sizeSlider = this.container?.querySelector('#size-slider');
                if (sizeSlider) {
                    const maxSize = this.calculateMaxSizeUnrestricted() || 1; // Use 1 only when truly 0 (no balance/price)
                    sizeSlider.max = maxSize;
                    sizeSlider.step = this.getSizeStep();
                }
            }

            // Auto-fill size with 0.5% risk calculation when balance becomes available (unless blocked after order)
            if (!this.blockSliderUpdates) {
                if (!this.isSizeManuallySet && this.availableBalance > 0) {
                    this.fillRiskBasedSize(0.005); // 0.5% risk
                } else if (!this.isSizeManuallySet) {
                    this.updateSizeSlider();
                }
            }
            // console.log('[TERMINAL] 💰 Size updated with risk-based calculation');
        } else {
            // console.warn('[TERMINAL] ⚠️ Invalid balance value:', balance, 'parsed as:', num);
        }
    }

    updateCurrentPrice(price) {
        // console.log('[TERMINAL] 💵 updateCurrentPrice called with:', price);
        if (price && price > 0) {
            this.currentPrice = price;
            // console.log('[TERMINAL] 💵 Price set to:', this.currentPrice);
            this.updateMarginDisplay();

            // ALWAYS update slider max attribute when price changes (unless blocked after order)
            // This ensures slider is never locked at 0
            if (!this.blockSliderUpdates) {
                const sizeSlider = this.container?.querySelector('#size-slider');
                if (sizeSlider) {
                    const maxSize = this.calculateMaxSizeUnrestricted() || 1; // Use 1 only when truly 0 (no balance/price)
                    sizeSlider.max = maxSize;
                    sizeSlider.step = this.getSizeStep();
                }

                // Only update slider value if user hasn't manually set size
                if (!this.isSizeManuallySet) {
                    this.updateSizeSlider();
                }
            }
            // console.log('[TERMINAL] 💵 Size slider updated');

            // Update current asset data for other calculations
            if (this.currentAssetData) {
                this.currentAssetData.currentPrice = price;
            }

            // Update price input for market orders to show current price with 5 sig digits
            if (this.orderType === 'market') {
                const priceInput = this.container.querySelector('#price-input');
                if (priceInput && priceInput.disabled) {
                    priceInput.value = this.formatToSignificantDigits(price, 5);
                }
            }

            this.updateSizeUSDDisplay();

            // Update quick price button for limit orders when market price changes
            if (this.orderType === 'limit') {
                this.updateQuickPriceButton();
            }
        } else {
            // console.warn('[TERMINAL] ⚠️ Invalid price value:', price);
        }
    }

    /**
     * Set price from orderbook click (limit/chase orders only)
     * Called when user clicks orderbook price level
     */
    setPriceFromOrderbook(price) {
        // Guard: Only for limit and advanced orders
        if (this.orderType === 'market') {
            return;
        }

        // Format price to 5 significant digits
        const formattedPrice = this.formatToSignificantDigits(price, 5);

        // Update internal state
        this.price = formattedPrice;

        // Update DOM input directly (no re-render needed)
        const priceInput = this.container.querySelector('#price-input');
        if (priceInput && !priceInput.disabled) {
            priceInput.value = formattedPrice;
        }

        // Trigger validation and calculations
        this.validatePrice();
        this.validateTPSL();
        this.calculateMargin();
    }

    updateBalanceDisplay() {
        const balanceElement = this.container.querySelector('#available-balance');
        if (balanceElement) {
            balanceElement.textContent = `$${this.availableBalance.toFixed(2)}`;
        }
    }

    /**
     * Clear price data to prevent stale calculations during asset switching
     */
    clearPriceData() {
        this.currentPrice = 0;
        if (this.currentAssetData) {
            this.currentAssetData.currentPrice = 0;
        }
    }


    destroy() {
        // Cleanup subscription without affecting main stream
        if (this.balanceUnsubscribe) {
            this.balanceUnsubscribe();
            this.balanceUnsubscribe = null;
        }

    }

    async submitOrder() {
        // Check cooldown to prevent double-submission
        const now = Date.now();
        if (now - this.lastSubmitTime < this.submitCooldown) {
            return; // Still in cooldown period
        }

        // Get button reference and check if already disabled
        const submitBtn = this.container.querySelector('#submit-order');
        if (!submitBtn || submitBtn.disabled) return;

        // Validate all inputs before submission
        this.validateInputs();

        // Check for validation errors (only count elements with error class AND non-empty text)
        const errors = this.container.querySelectorAll('.input-validation.error');
        const errorTexts = Array.from(errors).map(e => e.textContent.trim()).filter(t => t);

        // Filter out known false positive messages
        const realErrors = errorTexts.filter(msg =>
            !msg.includes('Size must be greater than 0')
        );

        if (realErrors.length > 0) {
            // Show notification for validation errors
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'error',
                    title: '❌ Validation Error',
                    message: realErrors.join(', '),
                    duration: 4000
                });
            }
            return; // Don't start cooldown if validation fails
        }

        // Validate chase settings before submit if chase order
        if (this.orderType === 'advanced' && this.advancedType === 'chase') {
            if (!this.validateChaseSettings()) {
                if (window.NotificationManager) {
                    window.NotificationManager.showNotification({
                        type: 'error',
                        title: '❌ Invalid Chase Settings',
                        message: 'Please check chase configuration values',
                        duration: 5000
                    });
                }
                return;
            }
        }

        // Disable button and update UI
        submitBtn.disabled = true;
        submitBtn.classList.add('submitting');
        const submitText = submitBtn.querySelector('.submit-text');
        const originalText = submitText.textContent;
        submitText.textContent = 'Submitting...';

        // Set cooldown timestamp
        this.lastSubmitTime = now;

        // Transform order data for comprehensive trading order manager
        // console.log('🔍 [TERMINAL-DEBUG] Current leverage state:', this.leverage, 'Type:', typeof this.leverage);
        // console.log('🔍 [TERMINAL-DEBUG] Selected asset:', this.selectedAsset);
        // console.log('🔍 [TERMINAL-DEBUG] Current asset data:', this.currentAssetData);
        // console.log('⚠️ [ORDER-SUBMIT] About to submit order for asset:', this.selectedAsset);

        // Check if layered order mode
        const isLayeredOrder = this.orderType === 'limit' && this.layerEnabled && this.layerCount > 1;

        const orderData = {
            asset: this.selectedAsset,
            orderSide: this.orderSide === 'long' ? 'buy' : 'sell', // Convert long/short to buy/sell
            orderType: this.orderType === 'advanced' ? 'limit' : this.orderType, // Advanced → limit
            price: this.orderType === 'market' ? null : (parseFloat(this.price) || null), // null for market, price for limit/advanced
            size: this.getOrderSizeInAsset(), // Get size in asset units
            leverage: this.leverage,
            isCrossMargin: this.isCrossMargin, // Pass margin mode preference
            reduceOnly: this.reduceOnlyEnabled || false, // Use checkbox state
            postOnly: false, // Removed advanced option
            timeInForce: this.orderType === 'market' ? 'IOC' : this.timeInForce, // IOC for market orders
            // For chase orders, TP/SL is handled post-fill by chase-tracker, not at order placement
            tpslEnabled: (this.orderType === 'advanced' && this.advancedType === 'chase') ? false : this.tpslEnabled,
            tpPrice: this.tpPrice ? parseFloat(this.tpPrice) : null,
            slPrice: this.slPrice ? parseFloat(this.slPrice) : null,
            tpslOrderType: 'limit', // Always limit orders (hardcoded in backend)

            // CHASE SETTINGS (new fields)
            isChaseOrder: this.orderType === 'advanced' && this.advancedType === 'chase',
            chaseSettings: (this.orderType === 'advanced' && this.advancedType === 'chase') ? (() => {
                const rawValue = (this.chaseSettings.tickDistance || '5').toString().trim().replace(',', '.');
                const isPercent = rawValue.endsWith('%');
                const numValue = parseFloat(rawValue.replace('%', ''));

                return {
                    tickDistance: isPercent ? null : numValue,
                    percentDistance: isPercent ? numValue : null,
                    isPercent: isPercent,
                    frequencyRangeMin: this.chaseSettings.frequencyRangeMin,
                    frequencyRangeMax: this.chaseSettings.frequencyRangeMax,
                    tpslEnabled: this.tpslEnabled,
                    tpAtrMultiple: this.chaseSettings.tpAtrMultiple,
                    slAtrMultiple: this.chaseSettings.slAtrMultiple,
                    // Price limit: stops chase if price exceeds (Long) or drops below (Short)
                    rangePrice: this.chaseSettings.priceLimitEnabled ? this.chaseSettings.priceLimit : null,
                    rangeType: this.orderSide === 'long' ? 'upper' : 'lower'
                };
            })() : null,

            // LAYER SETTINGS (new fields)
            isLayeredOrder: isLayeredOrder,
            layerSettings: isLayeredOrder ? {
                layerCount: this.layerCount,
                tickDistance: this.layerTickDistance
            } : null
        };

        // Debug log for chase orders
        if (orderData.isChaseOrder) {
            console.log(`[CHASE-DEBUG] 📋 CHASE ORDER CREATED`, {
                isChaseOrder: orderData.isChaseOrder,
                asset: orderData.asset,
                side: orderData.orderSide,
                size: orderData.size,
                price: orderData.price,
                chaseSettings: orderData.chaseSettings
            });
        }


        try {
            // Submit order via new comprehensive trading order manager
            const isElectron = window.process?.versions?.electron;
            let result;

            // Handle layered orders - submit multiple orders at different price levels
            if (isLayeredOrder) {
                result = await this.submitLayeredOrders(orderData);
            } else {
                // Single order submission
                if (!isElectron) {
                    // Browser mode - use BrowserHyperliquidClient
                    if (orderData.isChaseOrder) {
                        console.log(`[CHASE-DEBUG] 📤 Submitting chase order via BrowserHyperliquidClient...`);
                    }
                    result = await window.BrowserHyperliquidClient.executeTradingOrder(orderData);
                    if (orderData.isChaseOrder) {
                        console.log(`[CHASE-DEBUG] 📥 Order submission result:`, result);
                    }
                } else {
                    // Electron mode - use IPC
                    result = await window.electronAPI.invoke('hyperliquid:executeTradingOrder', orderData);
                }
            }

            if (result.success) {
                // Use existing NotificationManager system like quickbuy
                if (window.NotificationManager) {
                    // Determine order type display text
                    let orderTypeDisplay = orderData.orderType;
                    if (orderData.isChaseOrder) orderTypeDisplay = 'Chase';
                    if (result.isLayered) orderTypeDisplay = 'Layered Limit';

                    // Build details array
                    const details = [
                        `Order Type: ${orderTypeDisplay}`,
                        `CLOID: ${result.cloid}`,
                        `Orders: ${result.orderCount}`
                    ];

                    if (orderData.isChaseOrder) {
                        details.push(`Tick Distance: ${orderData.chaseSettings.tickDistance}`);
                    }

                    if (result.isLayered) {
                        details.push(`Layers: ${result.totalLayers}`);
                        if (result.failedLayers > 0) {
                            details.push(`Failed: ${result.failedLayers}`);
                        }
                    }

                    window.NotificationManager.showNotification({
                        type: 'success',
                        title: '✅ Trading Order Placed',
                        message: `${orderData.orderSide.toUpperCase()} ${orderData.size} ${orderData.asset} @ ${orderTypeDisplay}`,
                        details: details,
                        duration: 5000
                    });
                }

                // Handle chase order - start watcher
                if (orderData.isChaseOrder && orderData.chaseSettings) {
                    console.log(`[CHASE-DEBUG] 🏁 Chase order detected, preparing to start chase watcher`);
                    console.log(`[CHASE-DEBUG] 📦 Full result from order:`, result);

                    // Extract SDK OID from response (most reliable identifier)
                    const sdkOid = result?.result?.response?.data?.statuses?.[0]?.resting?.oid ||
                                   result?.result?.response?.data?.statuses?.[0]?.filled?.oid ||
                                   result?.orderId ||
                                   result?.oid;

                    console.log(`[CHASE-DEBUG] 🔑 Extracted SDK OID: ${sdkOid}`);

                    const pendingChaseData = {
                        cloid: result.cloid,
                        sdkOid: sdkOid ? String(sdkOid) : null, // SDK OID for direct matching
                        orderData: orderData,
                        settings: orderData.chaseSettings, // From inline inputs, not modal
                        timestamp: Date.now(),
                        asset: orderData.asset,
                        side: orderData.orderSide,
                        size: orderData.size,
                        price: orderData.price
                    };

                    console.log(`[CHASE-DEBUG] 📝 pendingChaseData created:`, pendingChaseData);

                    this.pendingChases.push(pendingChaseData);
                    console.log(`[CHASE-DEBUG] ➕ Added to pendingChases, total: ${this.pendingChases.length}`);

                    this.waitForOrderAndStartChase(pendingChaseData);
                } else if (orderData.isChaseOrder) {
                    console.warn(`[CHASE-DEBUG] ⚠️ isChaseOrder=true but no chaseSettings!`);
                }

                // Trigger full DEX poll after order placed (0.5s delay for API propagation)
                setTimeout(async () => {
                    try {
                        const isElectron = window.process?.versions?.electron;
                        if (!isElectron && window.BrowserPositionTracker) {
                            await window.BrowserPositionTracker.pollAllDexs();
                        } else if (isElectron) {
                            await window.electronAPI.invoke('positions:refresh');
                        }
                    } catch (error) {
                        // console.error('[TERMINAL] Poll error after order placement:', error);
                    }
                }, 500);

                // Don't reset form - preserve user's order type, TP/SL settings, etc.
                // Only clear price for limit orders (market orders don't have manual price)
                if (this.orderType === 'limit' || (this.orderType === 'advanced' && this.advancedType !== 'evgrid')) {
                    this.price = '';  // Clear limit price for next order
                }
                // Block programmatic slider updates for 3 seconds to prevent jump
                this.blockSliderUpdates = true;
                setTimeout(() => {
                    this.blockSliderUpdates = false;
                    this.render();  // Refresh UI after blocking period
                }, 3000);
            } else {
                // Use existing NotificationManager system for errors
                if (window.NotificationManager) {
                    // Detect HIP-3 asset and margin error
                    const isHip3 = orderData.asset.includes(':');
                    const errorString = result.error || result.message || 'Unknown error';
                    const isMarginError = errorString.toLowerCase().includes('margin') ||
                        errorString.toLowerCase().includes('insufficient');

                    let errorTitle = '❌ Trading Order Failed';
                    let errorMessage = errorString;

                    if (isHip3 && isMarginError) {
                        errorTitle = '❌ HIP-3 Order Failed - Insufficient USDH';
                        errorMessage = `HIP-3 assets require USDH for margin.\n\nError: ${errorString}\n\n💡 Check your USDH balance in the Account Summary above.`;
                    }

                    window.NotificationManager.showNotification({
                        type: 'error',
                        title: errorTitle,
                        message: errorMessage,
                        duration: isHip3 && isMarginError ? 10000 : 8000
                    });
                }
            }
        } catch (error) {
            // Use existing NotificationManager system for exceptions
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'error',
                    title: '❌ Order Submission Error',
                    message: error.message,
                    duration: 8000
                });
            }
        } finally {
            // Re-enable button after cooldown (always runs even if error)
            setTimeout(() => {
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.classList.remove('submitting');
                    if (submitText) {
                        submitText.textContent = originalText;
                    }
                }
            }, this.submitCooldown);
        }
    }

    /**
     * Parse tick distance value - supports both ticks (number) and percentage (X%)
     * @param {string|number} value - e.g., "10" for 10 ticks, "1%" for 1% distance
     * @param {number} basePrice - Base price for percentage calculation
     * @param {number} tickSize - Calculated tick size for the asset
     * @returns {number} Price offset per layer
     */
    parseTickDistance(value, basePrice, tickSize = 0.01) {
        const strValue = String(value).trim();

        if (strValue.endsWith('%')) {
            // Percentage mode: e.g., "1%" means 1% of base price
            const pct = parseFloat(strValue.slice(0, -1)) || 1;
            return basePrice * (pct / 100);
        } else {
            // Tick mode: e.g., "10" means 10 ticks
            const ticks = parseInt(strValue) || 10;
            return ticks * tickSize;
        }
    }

    /**
     * Submit layered orders - multiple orders at different price levels
     * Uses batch order API for efficiency
     * For LONG: orders spread below base price (better entry prices)
     * For SHORT: orders spread above base price (better entry prices)
     */
    async submitLayeredOrders(baseOrderData) {
        const { layerSettings, price, size, orderSide, asset } = baseOrderData;
        const { layerCount, tickDistance } = layerSettings;
        const targetAsset = asset || this.selectedAsset;

        // Get current best bid/ask and calculate proper tick size
        const isElectron = window.process?.versions?.electron;
        let bidAsk;

        if (!isElectron && window.BrowserHyperliquidClient) {
            bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(targetAsset);
            await window.BrowserHyperliquidClient.getAssetIndex(targetAsset);
        } else if (isElectron) {
            bidAsk = await window.electronAPI.invoke('hyperliquid:getBestBidAsk', targetAsset);
        }

        if (!bidAsk || !bidAsk.bestBid || !bidAsk.bestAsk) {
            throw new Error('Failed to get market prices for layered orders');
        }

        // Calculate tick size from price decimals
        const bidStr = String(bidAsk.bestBid);
        const askStr = String(bidAsk.bestAsk);
        const bidDecimals = bidStr.includes('.') ? bidStr.split('.')[1].length : 0;
        const askDecimals = askStr.includes('.') ? askStr.split('.')[1].length : 0;
        const decimals = Math.max(bidDecimals, askDecimals);
        const tickSize = Math.pow(10, -decimals);

        // console.log(`[LAYERS] Calculated tick size: ${tickSize} for ${targetAsset}`);

        // Parse tick distance (supports ticks or %)
        const priceOffsetPerLayer = this.parseTickDistance(tickDistance, price, tickSize);
        const sizePerLayer = this.formatSizeToDecimals(size / Math.max(1, layerCount));
        const isLong = orderSide === 'buy';

        // console.log(`[LAYERS] Creating ${layerCount} orders, offset per layer: ${priceOffsetPerLayer}`);

        // Generate orders for batch placement
        const batchOrders = [];

        for (let i = 0; i < layerCount; i++) {
            // Calculate price offset: layer 0 at base price, subsequent layers spread out
            const priceOffset = i * priceOffsetPerLayer;

            let layerPrice;
            if (isLong) {
                layerPrice = price - priceOffset;  // Lower prices for long
            } else {
                layerPrice = price + priceOffset;  // Higher prices for short
            }

            // Round to tick size
            if (!isElectron && window.BrowserHyperliquidClient?.roundToTickSize) {
                layerPrice = window.BrowserHyperliquidClient.roundToTickSize(layerPrice);
            } else {
                layerPrice = Math.round(layerPrice / tickSize) * tickSize;
            }

            // console.log(`[LAYERS] Layer ${i}: price ${layerPrice}, size ${sizePerLayer}`);

            batchOrders.push({
                asset: targetAsset,
                side: orderSide,
                price: parseFloat(layerPrice),
                size: parseFloat(sizePerLayer),
                reduceOnly: baseOrderData.reduceOnly || false
            });
        }

        // Use batch order API
        try {
            const isElectron = window.process?.versions?.electron;
            let batchResult;

            if (!isElectron && window.BrowserHyperliquidClient?.batchPlace) {
                batchResult = await window.BrowserHyperliquidClient.batchPlace(batchOrders);
            } else if (isElectron) {
                batchResult = await window.electronAPI.invoke('hyperliquid:batchPlace', batchOrders);
            } else {
                // Fallback to individual orders if batch not available
                // console.warn('[TERMINAL] Batch order not available, falling back to individual orders');
                return this._submitLayeredOrdersIndividually(baseOrderData, batchOrders);
            }

            // Parse batch result
            const statuses = batchResult?.response?.data?.statuses || [];
            let successCount = 0;
            let failCount = 0;

            statuses.forEach((status, i) => {
                if (status.resting || status.filled) {
                    successCount++;
                } else if (status.error) {
                    failCount++;
                    // console.warn(`[TERMINAL] Layer ${i + 1} failed: ${status.error}`);
                }
            });

            // If no statuses, count all as failed
            if (statuses.length === 0) {
                failCount = layerCount;
            }

            // After entry orders placed, submit TP/SL if enabled
            let tpslCount = 0;
            if (successCount > 0 && baseOrderData.tpslEnabled) {
                // Calculate total size from all successful layers
                const totalSize = successCount * sizePerLayer;
                // console.log(`[LAYERS] Placing TP/SL for total size: ${totalSize} (${successCount} layers × ${sizePerLayer}`);

                try {
                    // Get asset index
                    const assetIndex = await window.BrowserHyperliquidClient.getAssetIndex(targetAsset);
                    const cloid = window.BrowserHyperliquidClient.generateCLOID();

                    // Manually build TP/SL trigger orders
                    const tpslOrders = [];

                    // Build TP order (trigger order, reduce only)
                    if (baseOrderData.tpPrice) {
                        const tpPrice = parseFloat(baseOrderData.tpPrice);
                        const roundedTpPrice = window.BrowserHyperliquidClient.roundToTickSize(tpPrice);
                        const roundedSize = window.BrowserHyperliquidClient.roundToSizeDecimals(totalSize);

                        tpslOrders.push({
                            a: assetIndex,
                            b: orderSide !== 'buy', // Opposite direction
                            p: String(roundedTpPrice),
                            s: String(roundedSize),
                            r: true, // Always reduce-only for TP/SL
                            t: {
                                trigger: {
                                    isMarket: false, // TP uses limit
                                    triggerPx: String(roundedTpPrice),
                                    tpsl: 'tp'
                                }
                            },
                            c: cloid
                        });
                    }

                    // Build SL order (trigger order, reduce only)
                    if (baseOrderData.slPrice) {
                        const slPrice = parseFloat(baseOrderData.slPrice);
                        const roundedSlPrice = window.BrowserHyperliquidClient.roundToTickSize(slPrice);
                        const roundedSize = window.BrowserHyperliquidClient.roundToSizeDecimals(totalSize);

                        tpslOrders.push({
                            a: assetIndex,
                            b: orderSide !== 'buy', // Opposite direction
                            p: String(roundedSlPrice),
                            s: String(roundedSize),
                            r: true, // Always reduce-only for TP/SL
                            t: {
                                trigger: {
                                    isMarket: true, // SL uses market for guaranteed execution
                                    triggerPx: String(roundedSlPrice),
                                    tpsl: 'sl'
                                }
                            },
                            c: cloid
                        });
                    }

                    // Submit TP/SL trigger orders
                    if (tpslOrders.length > 0) {
                        const tpslResult = await window.BrowserHyperliquidClient.exchClient.order({
                            orders: tpslOrders,
                            grouping: 'na',
                            builder: {
                                b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                                f: 10  // 0.01%
                            }
                        });

                        const tpslStatuses = tpslResult?.response?.data?.statuses || [];
                        tpslCount = tpslStatuses.filter(s => s.resting).length;
                        // console.log(`[LAYERS] TP/SL placed: ${tpslCount}/${tpslOrders.length}`);
                    }
                } catch (tpslError) {
                    // console.warn('[LAYERS] TP/SL placement failed:', tpslError);
                }
            }

            return {
                success: successCount > 0,
                orderCount: successCount + tpslCount,
                totalLayers: layerCount,
                failedLayers: failCount,
                cloid: statuses[0]?.resting?.oid || 'layered',
                message: `${successCount}/${layerCount} layer orders placed${tpslCount > 0 ? ` + ${tpslCount} TP/SL` : ''} (batch)`,
                isLayered: true,
                isBatch: true
            };
        } catch (error) {
            // console.error('[TERMINAL] Batch order failed:', error);
            return {
                success: false,
                orderCount: 0,
                totalLayers: layerCount,
                failedLayers: layerCount,
                error: error.message,
                message: `Batch order failed: ${error.message}`,
                isLayered: true
            };
        }
    }

    /**
     * Fallback: Submit layered orders individually (if batch not available)
     */
    async _submitLayeredOrdersIndividually(baseOrderData, orders) {
        const isElectron = window.process?.versions?.electron;
        let successCount = 0;
        let failCount = 0;

        for (const order of orders) {
            try {
                const orderData = {
                    ...baseOrderData,
                    price: order.price,
                    size: order.size,
                    isLayeredOrder: false,
                    layerSettings: null
                };

                let orderResult;
                if (!isElectron) {
                    orderResult = await window.BrowserHyperliquidClient.executeTradingOrder(orderData);
                } else {
                    orderResult = await window.electronAPI.invoke('hyperliquid:executeTradingOrder', orderData);
                }

                if (orderResult.success) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (error) {
                failCount++;
            }
        }

        return {
            success: successCount > 0,
            orderCount: successCount,
            totalLayers: orders.length,
            failedLayers: failCount,
            cloid: 'layered',
            message: `${successCount}/${orders.length} layer orders placed`,
            isLayered: true
        };
    }

    showNotification(message, type = 'info') {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `notification ${type}`;
        notification.textContent = message;
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 20px;
            border-radius: 4px;
            color: white;
            font-weight: 500;
            z-index: 10000;
            max-width: 300px;
            word-wrap: break-word;
            ${type === 'success' ? 'background-color: #10b981;' : ''}
            ${type === 'error' ? 'background-color: #ef4444;' : ''}
            ${type === 'info' ? 'background-color: #00BF63;' : ''}
        `;

        document.body.appendChild(notification);

        // Remove after 5 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 5000);
    }

    resetForm() {
        // Reset form inputs to defaults but preserve size for consecutive orders
        // this.size = ''; // Don't reset size - keep it for next order
        this.price = '';
        this.tpPrice = '';
        this.slPrice = '';
        this.tpslEnabled = false;

        // Reset to limit order (don't clear order type entirely)
        this.orderType = 'limit';
        this.advancedType = null;

        // Reset chase settings to defaults (keep values for convenience)
        // this.chaseSettings remains with last values for convenience

        this.render();
    }

    /**
     * Update the selected asset and refresh terminal
     */
    async updateAsset(newAsset, availableAssets = null) {
        this.selectedAsset = newAsset;
        if (availableAssets) {
            this.availableAssets = availableAssets;
        }
        this.updateAssetData();
        this.render();
        this.updateLeverageDisplay();
        // Don't call loadAccountData() here - it resets balance to 0
        // Balance is managed by updateAvailableBalance() from positions data
    }

    /**
     * Wait for order confirmation and start chase
     */
    async waitForOrderAndStartChase(pendingChaseData) {
        const maxWaitTime = 15000;  // 15 seconds max wait
        const pollInterval = 500;    // Check every 500ms
        const startTime = Date.now();

        // Create watcher ID
        const watcherId = `watcher-${pendingChaseData.cloid}`;

        console.log(`[CHASE-DEBUG] 🚀 waitForOrderAndStartChase called`, {
            watcherId,
            asset: pendingChaseData.asset,
            side: pendingChaseData.side,
            size: pendingChaseData.size,
            sdkOid: pendingChaseData.sdkOid,
            cloid: pendingChaseData.cloid,
            settings: pendingChaseData.settings
        });

        // Store watcher data
        this.chaseOrderWatchers.set(watcherId, {
            pendingChase: pendingChaseData,
            startTime: startTime,
            attempts: 0,
            status: 'waiting'
        });

        const checkOrder = async () => {
            const watcher = this.chaseOrderWatchers.get(watcherId);
            if (!watcher || watcher.status === 'cancelled') {
                console.log(`[CHASE-DEBUG] ❌ Watcher cancelled or not found: ${watcherId}`);
                return;
            }

            watcher.attempts++;
            console.log(`[CHASE-DEBUG] 🔍 Checking for order (attempt ${watcher.attempts})...`);

            try {
                // Trigger fresh position data
                if (window.BrowserPositionTracker) {
                    console.log(`[CHASE-DEBUG] 📡 Polling BrowserPositionTracker...`);
                    await window.BrowserPositionTracker.poll();
                } else {
                    console.log(`[CHASE-DEBUG] ⚠️ BrowserPositionTracker not available`);
                }

                // Get orders from tracker
                if (!window.BrowserPositionTracker || !window.BrowserPositionTracker.orders) {
                    console.warn('[CHASE-DEBUG] ⚠️ BrowserPositionTracker.orders not available');
                    setTimeout(checkOrder, pollInterval);
                    return;
                }

                const orders = Array.from(window.BrowserPositionTracker.orders.values());
                console.log(`[CHASE-DEBUG] 📋 Found ${orders.length} orders in tracker:`, orders.map(o => ({
                    oid: o.oid,
                    coin: o.coin,
                    side: o.side,
                    size: o.size,
                    cloid: o.cloid
                })));

                // Match by multiple criteria for reliability (in priority order)
                const ourOrder = orders.find(o => {
                    // Method 1: Match by SDK OID (MOST reliable - direct from exchange response)
                    if (pendingChaseData.sdkOid && String(o.oid) === pendingChaseData.sdkOid) {
                        console.log(`[CHASE-DEBUG] ✅ Matched by SDK OID: ${o.oid}`);
                        return true;
                    }

                    // Method 2: Match by CLOID (reliable but not always returned by tracker)
                    if (o.cloid && o.cloid === pendingChaseData.cloid) {
                        console.log(`[CHASE-DEBUG] ✅ Matched by CLOID: ${o.cloid}`);
                        return true;
                    }

                    // Method 3: Match by asset + side + size (fallback - less reliable)
                    // Only used if SDK OID and CLOID both unavailable
                    if (!pendingChaseData.sdkOid && !pendingChaseData.cloid) {
                        const assetMatch = o.coin === pendingChaseData.asset;
                        const sideMatch = (o.side === 'BUY' && pendingChaseData.side === 'buy') ||
                            (o.side === 'SELL' && pendingChaseData.side === 'sell');
                        const sizeMatch = o.size && pendingChaseData.size &&
                            Math.abs(parseFloat(o.size) - parseFloat(pendingChaseData.size)) / parseFloat(pendingChaseData.size) < 0.01;
                        if (assetMatch && sideMatch && sizeMatch) {
                            console.log(`[CHASE-DEBUG] ✅ Matched by asset+side+size fallback`);
                            return true;
                        }
                    }

                    return false;
                });

                if (ourOrder) {
                    // Order found! Start chase
                    console.log(`[CHASE-DEBUG] ✅ ORDER FOUND! Starting chase for OID: ${ourOrder.oid}`, ourOrder);

                    watcher.status = 'found';

                    // Start chase
                    await this.startChaseForConfirmedOrder(ourOrder.oid, ourOrder, pendingChaseData.settings);

                    // Clean up
                    this.pendingChases = this.pendingChases.filter(pc => pc.cloid !== pendingChaseData.cloid);
                    this.chaseOrderWatchers.delete(watcherId);
                    return;
                }

                console.log(`[CHASE-DEBUG] 🔄 Order not found yet. Looking for: sdkOid=${pendingChaseData.sdkOid}, cloid=${pendingChaseData.cloid}, asset=${pendingChaseData.asset}`);

                // Check timeout
                if (Date.now() - startTime > maxWaitTime) {
                    console.warn(`[CHASE-DEBUG] ⏰ TIMEOUT waiting for order after ${maxWaitTime}ms, ${watcher.attempts} attempts`);

                    watcher.status = 'timeout';

                    // Show warning notification
                    if (window.NotificationManager) {
                        window.NotificationManager.showNotification({
                            type: 'warning',
                            title: '⚠️ Chase Order Placed',
                            message: 'Order submitted but chase may not auto-start. You can manually enable chase from the orders list.',
                            details: [
                                `Asset: ${pendingChaseData.asset}`,
                                `Size: ${pendingChaseData.size}`,
                                `Attempts: ${watcher.attempts}`
                            ],
                            duration: 8000
                        });
                    }

                    // Clean up
                    this.pendingChases = this.pendingChases.filter(pc => pc.cloid !== pendingChaseData.cloid);
                    this.chaseOrderWatchers.delete(watcherId);
                    return;
                }

                // Continue polling
                console.log(`[CHASE-DEBUG] 🔄 Scheduling next check in ${pollInterval}ms...`);
                setTimeout(checkOrder, pollInterval);

            } catch (error) {
                console.error('[CHASE-DEBUG] ❌ Error checking order:', error);

                // Retry on error unless timeout reached
                if (Date.now() - startTime < maxWaitTime) {
                    setTimeout(checkOrder, pollInterval);
                } else {
                    console.error(`[CHASE-DEBUG] ❌ Max retries reached with error`);
                    watcher.status = 'error';
                    this.chaseOrderWatchers.delete(watcherId);
                    this.pendingChases = this.pendingChases.filter(pc => pc.cloid !== pendingChaseData.cloid);
                }
            }
        };

        // Start checking after 1 second (give API time to process)
        console.log(`[CHASE-DEBUG] ⏳ Starting order check in 1 second...`);
        setTimeout(checkOrder, 1000);
    }

    /**
     * Start chase for confirmed order
     */
    async startChaseForConfirmedOrder(orderId, orderData, settings) {
        console.log(`[CHASE-DEBUG] 🎯 startChaseForConfirmedOrder called`, {
            orderId,
            orderData,
            settings
        });

        try {
            // Check if BrowserChaseTracker is available
            if (!window.BrowserChaseTracker) {
                console.error(`[CHASE-DEBUG] ❌ BrowserChaseTracker not available!`);
                throw new Error('BrowserChaseTracker not available');
            }

            console.log(`[CHASE-DEBUG] ✅ BrowserChaseTracker is available`);

            // Normalize order data for chase tracker
            const normalizedOrderData = {
                coin: orderData.coin,
                asset: orderData.coin,
                side: orderData.side === 'BUY' ? 'B' : 'A',
                sz: orderData.size,
                size: orderData.size,
                limitPx: orderData.limitPx,
                price: orderData.limitPx,
                oid: orderId
            };

            console.log(`[CHASE-DEBUG] 📤 Calling BrowserChaseTracker.startChase with:`, {
                orderId,
                normalizedOrderData,
                settings
            });

            // Start chase via BrowserChaseTracker
            const result = await window.BrowserChaseTracker.startChase(
                orderId,
                normalizedOrderData,
                settings
            );

            console.log(`[CHASE-DEBUG] 📥 BrowserChaseTracker.startChase result:`, result);

            if (result.success) {
                console.log(`[CHASE-DEBUG] ✅ Chase started successfully: ${result.chaseId}`);

                // Show success notification
                if (window.NotificationManager) {
                    window.NotificationManager.showNotification({
                        type: 'success',
                        title: '✅ Chase Started',
                        message: `Chase active for ${orderData.coin} order`,
                        details: [
                            `Chase ID: ${result.chaseId}`,
                            `Tick Distance: ${settings.tickDistance}`,
                            `Frequency: ${settings.frequencyRangeMin}-${settings.frequencyRangeMax}s`
                        ],
                        duration: 5000
                    });
                }

                // Chase settings remain in this.chaseSettings for reuse

            } else {
                console.error(`[CHASE-DEBUG] ❌ Chase failed to start: ${result.error}`);

                // Show error notification
                if (window.NotificationManager) {
                    window.NotificationManager.showNotification({
                        type: 'error',
                        title: '❌ Chase Failed to Start',
                        message: `Order placed but chase failed: ${result.error}`,
                        details: [
                            `Order ID: ${orderId}`,
                            `You can manually start chase from the orders list`
                        ],
                        duration: 8000
                    });
                }
            }

        } catch (error) {
            console.error('[CHASE-DEBUG] ❌ Exception in startChaseForConfirmedOrder:', error);

            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'error',
                    title: '❌ Chase Error',
                    message: error.message,
                    duration: 8000
                });
            }
        }
    }

    /**
     * Destroy the trading terminal and clean up event listeners
     */
    destroy() {
        // console.log('🗑️ [TRADING-TERMINAL] Destroying instance for asset:', this.selectedAsset);

        // Cancel all pending chase watchers
        for (const [watcherId, watcher] of this.chaseOrderWatchers.entries()) {
            watcher.status = 'cancelled';
            // console.log(`[TERMINAL] Cancelled chase watcher: ${watcherId}`);
        }
        this.chaseOrderWatchers.clear();
        this.pendingChases = [];

        // Remove global reference
        const globalKey = `tradingTerminal_${this.selectedAsset?.replace(/[^a-zA-Z0-9]/g, '_')}`;
        if (window[globalKey]) {
            delete window[globalKey];
        }

        // Remove all event listeners by replacing container with clean clone
        if (this.container && this.container.parentNode) {
            const newContainer = this.container.cloneNode(false);
            this.container.parentNode.replaceChild(newContainer, this.container);
        }

        // Clear all references
        this.container = null;
        this.selectedAsset = null;
        this.availableAssets = null;
        this.currentAssetData = null;

        // console.log('✅ [TRADING-TERMINAL] Instance destroyed');
    }

    /**
     * Initialize leverage modal
     */
    initializeLeverageModal() {
        // Modal is already rendered in HTML, just need to set up event listeners
        // Event listeners are handled in setupEventListeners()
    }

    /**
     * Open grid strategy modal
     */
    openGridModal() {
        if (window.GridSettingsModal && this.selectedAsset) {
            window.GridSettingsModal.open(this.selectedAsset, (gridId) => {
                // console.log(`[TERMINAL] Grid started: ${gridId}`);
            });
        } else {
            // console.error('[TERMINAL] GridSettingsModal not available or no asset selected');
        }
    }

    /**
     * Get EV Grid levels preview text
     */
    _getEvGridLevelsPreview() {
        const levels = this.evGridSettings.levels || 3;
        const rawValue = (this.evGridSettings.baseTickDistance || '10').toString().trim().replace(',', '.');
        const isPercent = rawValue.endsWith('%');
        const baseValue = parseFloat(rawValue.replace('%', '')) || (isPercent ? 0.1 : 10);

        const levelValues = [];
        for (let i = 1; i <= levels; i++) {
            if (isPercent) {
                levelValues.push((baseValue * i).toFixed(2) + '%');
            } else {
                levelValues.push(baseValue * i);
            }
        }
        const unit = isPercent ? '' : ' ticks';
        return levelValues.join(', ') + unit + ' from market';
    }

    /**
     * Get EV Grid summary text
     */
    _getEvGridSummary() {
        const levels = this.evGridSettings.levels || 3;
        const size = this.evGridSettings.sizePerLevel || 0;
        const totalOrders = levels * 2;
        const totalSize = size * totalOrders;
        return `
            <div>${levels} BUY + ${levels} SELL = ${totalOrders} orders</div>
            <div>Total size: ${totalSize.toFixed(4)} ${this.selectedAsset || ''}</div>
        `;
    }

    /**
     * Update EV Grid preview elements
     */
    updateEvGridPreview() {
        const previewEl = this.container.querySelector('#evgrid-levels-preview');
        if (previewEl) {
            previewEl.textContent = 'Levels: ' + this._getEvGridLevelsPreview();
        }
        const summaryEl = this.container.querySelector('#evgrid-summary');
        if (summaryEl) {
            summaryEl.innerHTML = this._getEvGridSummary();
        }
        this.calculateMargin();
    }

    /**
     * Pre-fill EV Grid ranges from current orderbook
     */
    async prefillEvGridAnchor() {
        try {
            if (!this.selectedAsset || !window.BrowserHyperliquidClient) return;

            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(this.selectedAsset);
            if (bidAsk && bidAsk.mid) {
                const midPrice = bidAsk.mid;
                const decimals = this._getPriceDecimals(midPrice);

                this.evGridSettings.anchor = parseFloat(midPrice.toFixed(decimals));

                // Update input if it exists
                const anchorInput = this.container.querySelector('#evgrid-anchor-input');
                if (anchorInput) anchorInput.value = this.evGridSettings.anchor;
            }
        } catch (e) {
            // console.log('[TERMINAL] Error prefilling EV Grid anchor:', e);
        }
    }

    /**
     * Get price decimals
     */
    _getPriceDecimals(price) {
        const priceStr = String(price);
        if (priceStr.includes('.')) {
            return priceStr.split('.')[1].length;
        }
        return 2;
    }

    /**
     * Start EV Grid strategy
     */
    async startEvGrid() {
        // Debounce: prevent double-click
        if (this._evGridStarting) {
            // console.log('[TERMINAL] EV Grid start already in progress, ignoring');
            return;
        }
        this._evGridStarting = true;

        try {
            if (!window.GridStrategy) {
                alert('Grid strategy not available');
                return;
            }

            // Validate inputs
            if (!this.evGridSettings.sizePerLevel || this.evGridSettings.sizePerLevel <= 0) {
                alert('Please enter a valid size per level');
                return;
            }

            // Validate and parse tick/percentage distance
            const rawDistance = (this.evGridSettings.baseTickDistance || '10').toString().trim().replace(',', '.');
            const isPercent = rawDistance.endsWith('%');
            const numValue = parseFloat(rawDistance.replace('%', ''));

            if (isPercent) {
                if (isNaN(numValue) || numValue <= 0 || numValue > 10) {
                    alert('Percentage distance must be between 0.01% and 10%');
                    return;
                }
            } else {
                if (isNaN(numValue) || numValue < 1 || numValue > 10000) {
                    alert('Tick distance must be between 1 and 10000');
                    return;
                }
            }

            // Disable button
            const btn = this.container.querySelector('#start-evgrid-btn');
            if (btn) {
                btn.disabled = true;
                btn.querySelector('.submit-text').textContent = 'Starting...';
            }

            const config = {
                asset: this.selectedAsset,
                levels: this.evGridSettings.levels,
                baseTickDistance: isPercent ? null : numValue,
                basePercentDistance: isPercent ? numValue : null,
                isPercent: isPercent,
                sizePerLevel: this.evGridSettings.sizePerLevel,
                anchor: this.evGridSettings.useAnchor ? this.evGridSettings.anchor : null,  // null = use mid price
                chaseSettings: {
                    frequencyRangeMin: this.evGridSettings.frequency,
                    frequencyRangeMax: this.evGridSettings.frequency,
                    tpslEnabled: this.tpslEnabled,
                    tpAtrMultiple: this.chaseSettings.tpAtrMultiple,
                    slAtrMultiple: this.chaseSettings.slAtrMultiple
                }
            };

            // console.log('[TERMINAL] Starting EV Grid with config:', config);

            const result = await window.GridStrategy.startGrid(config);

            // Silent ignore if duplicate call
            if (result.duplicate) {
                // console.log('[TERMINAL] EV Grid start was duplicate, ignoring');
                return;
            }

            if (result.success) {
                if (window.NotificationManager) {
                    window.NotificationManager.showSimpleNotification(
                        `EV Grid started: ${result.ordersPlaced} orders placed`,
                        'success',
                        3000
                    );
                }
            } else if (result.error) {
                if (window.NotificationManager) {
                    window.NotificationManager.showSimpleNotification(
                        `Failed to start EV Grid: ${result.error}`,
                        'error',
                        5000
                    );
                }
            }

            // Re-enable button
            if (btn) {
                btn.disabled = false;
                btn.querySelector('.submit-text').textContent = 'Start EV Grid';
            }

        } catch (error) {
            // console.error('[TERMINAL] Error starting EV Grid:', error);
            if (window.NotificationManager) {
                window.NotificationManager.showSimpleNotification(
                    `Error starting EV Grid: ${error.message}`,
                    'error',
                    5000
                );
            }

            const btn = this.container.querySelector('#start-evgrid-btn');
            if (btn) {
                btn.disabled = false;
                btn.querySelector('.submit-text').textContent = 'Start EV Grid';
            }
        } finally {
            this._evGridStarting = false;
        }
    }

    /**
     * Open leverage modal
     */
    openLeverageModal() {
        const modal = this.container.querySelector('#leverage-modal-overlay');
        const slider = this.container.querySelector('#leverage-slider-modal');
        const valueDisplay = this.container.querySelector('#leverage-value-modal');

        if (modal && slider && valueDisplay) {
            // Set current leverage value
            slider.value = this.leverage;
            slider.max = this.currentAssetData?.maxLeverage || 50;
            valueDisplay.textContent = `${this.leverage}x`;

            // Update markers
            const markers = slider.parentElement.querySelectorAll('.leverage-markers .marker');
            if (markers.length >= 3) {
                const maxLeverage = this.currentAssetData?.maxLeverage || 50;
                markers[1].textContent = `${Math.floor((1 + maxLeverage) / 2)}x`;
                markers[2].textContent = `${maxLeverage}x`;
            }

            modal.style.display = 'flex';
        }
    }

    /**
     * Close leverage modal without saving
     */
    closeLeverageModal() {
        const modal = this.container.querySelector('#leverage-modal-overlay');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    /**
     * Confirm and apply leverage from modal
     */
    confirmLeverageModal() {
        const slider = this.container.querySelector('#leverage-slider-modal');
        if (slider) {
            const newLeverage = parseInt(slider.value);
            const maxLeverage = this.currentAssetData?.maxLeverage || 50;
            this.leverage = Math.min(newLeverage, maxLeverage);

            // Update leverage button text
            this.updateLeverageDisplay();

            // Update margin display and size slider
            this.updateMarginDisplay();
            this.updateSizeSlider();
            // Update submit button state after leverage change
            this.updateSubmitButtonState();
        }

        this.closeLeverageModal();
    }

    /**
     * Open margin mode confirmation modal
     */
    openMarginModeModal() {
        const modal = this.container.querySelector('#margin-mode-modal-overlay');
        const targetText = this.container.querySelector('#margin-mode-target');
        
        if (modal && targetText) {
            // Set target mode text (opposite of current)
            targetText.textContent = this.isCrossMargin ? 'Isolated' : 'Cross';
            modal.style.display = 'flex';
        }
    }

    /**
     * Close margin mode modal without changing
     */
    closeMarginModeModal() {
        const modal = this.container.querySelector('#margin-mode-modal-overlay');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    /**
     * Confirm margin mode change
     */
    confirmMarginModeModal() {
        // Toggle margin mode
        this.isCrossMargin = !this.isCrossMargin;
        
        // Save to localStorage
        localStorage.setItem('marginMode', this.isCrossMargin ? 'cross' : 'isolated');
        
        // Update button text
        const crossBtn = this.container.querySelector('#cross-btn');
        if (crossBtn) {
            crossBtn.textContent = this.isCrossMargin ? 'Cross' : 'Isolated';
        }
        
        // Close modal
        this.closeMarginModeModal();
    }

    getDisplaySize() {
        if (!this.size) return '';
        if (this.sizeUnit === 'usd') {
            if (!this.currentPrice) return '0.00';
            const val = parseFloat(this.size) * this.currentPrice;
            return isNaN(val) ? '0.00' : val.toFixed(2);
        }
        return this.size;
    }
}

export { TradingTerminal };

// Make TradingTerminal available globally
window.TradingTerminal = TradingTerminal;
